export type Style = {
  /**
   * The CSS `align-content` property sets the distribution of space between and around content items along a [flexbox](https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_flexible_box_layout)'s [cross axis](https://developer.mozilla.org/en-US/docs/Glossary/Cross_Axis), or a [grid](https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_grid_layout) or [block-level](https://developer.mozilla.org/en-US/docs/Glossary/Block-level_content) element's block axis.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/align-content)
   */
  alignContent?: string;
  /**
   * The CSS `align-items` property sets the [`align-self`](https://developer.mozilla.org/en-US/docs/Web/CSS/align-self) value on all direct children as a group. In flexbox, it controls the alignment of items on the [cross axis](https://developer.mozilla.org/en-US/docs/Glossary/Cross_Axis). In grid layout, it controls the alignment of items on the block axis within their [grid areas](https://developer.mozilla.org/en-US/docs/Glossary/Grid_Areas).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/align-items)
   */
  alignItems?: string;
  /**
   * The `alignment-baseline` attribute specifies how an object is aligned with respect to its parent. This property specifies which baseline of this element is to be aligned with the corresponding baseline of the parent. For example, this allows [alphabetic baselines](https://developer.mozilla.org/en-US/docs/Glossary/Baseline/Typography) in Roman text to stay aligned across font size changes. It defaults to the baseline with the same name as the computed value of the `alignment-baseline` property.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/alignment-baseline)
   */
  alignmentBaseline?:
    | "auto"
    | "baseline"
    | "before-edge"
    | "text-before-edge"
    | "middle"
    | "central"
    | "after-edge"
    | "text-after-edge"
    | "ideographic"
    | "alphabetic"
    | "hanging"
    | "mathematical"
    | "top"
    | "center"
    | "bottom";
  /**
   * The `align-self` CSS property overrides a grid or flex item's [`align-items`](https://developer.mozilla.org/en-US/docs/Web/CSS/align-items) value. In grid, it aligns the item inside the [grid area](https://developer.mozilla.org/en-US/docs/Glossary/Grid_Areas). In flexbox, it aligns the item on the [cross axis](https://developer.mozilla.org/en-US/docs/Glossary/Cross_Axis).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/align-self)
   */
  alignSelf?: string;
  /**
   * **Experimental:** This is an [experimental technology](https://developer.mozilla.org/en-US/docs/MDN/Writing_guidelines/Experimental_deprecated_obsolete#experimental) Check the [Browser compatibility table](#browser_compatibility) carefully before using this in production.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/align-tracks)
   */
  alignTracks?: string;
  /**
   * The `animation-delay` CSS property specifies the amount of time to wait from applying the animation to an element before beginning to perform the animation. The animation can start later, immediately from its beginning, or immediately and partway through the animation.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/animation-delay)
   */
  animationDelay?: string;
  /**
   * The `animation-direction` CSS property sets whether an animation should play forward, backward, or alternate back and forth between playing the sequence forward and backward.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/animation-direction)
   */
  animationDirection?: string;
  /**
   * The `animation-duration` CSS property sets the length of time that an animation takes to complete one cycle.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/animation-duration)
   */
  animationDuration?: string;
  /**
   * The `animation-fill-mode` CSS property sets how a CSS animation applies styles to its target before and after its execution.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/animation-fill-mode)
   */
  animationFillMode?: string;
  /**
   * The `animation-iteration-count` CSS property sets the number of times an animation sequence should be played before stopping.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/animation-iteration-count)
   */
  animationIterationCount?: string;
  /**
   * The `animation-name` CSS property specifies the names of one or more [`\@keyframes`](https://developer.mozilla.org/en-US/docs/Web/CSS/\@keyframes) at-rules that describe the animation to apply to an element. Multiple `\@keyframes` at-rules are specified as a comma-separated list of names. If the specified name does not match any `\@keyframes` at-rule, no properties are animated.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/animation-name)
   */
  animationName?: string;
  /**
   * The `animation-play-state` CSS property sets whether an animation is running or paused.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/animation-play-state)
   */
  animationPlayState?: string;
  /**
   * The `animation-timing-function` CSS property sets how an animation progresses through the duration of each cycle.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/animation-timing-function)
   */
  animationTimingFunction?: string;
  /**
   * The `appearance` CSS property is used to display UI elements with platform-specific styling, based on the operating system's theme.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/appearance)
   */
  appearance?: "none" | "auto";
  /**
   * The `aspect-ratio` CSS property allows you to define the desired width-to-height ratio of an element's box. This means that even if the parent container or viewport size changes, the browser will adjust the element's dimensions to maintain the specified width-to-height ratio. The specified [aspect ratio](https://developer.mozilla.org/en-US/docs/Glossary/Aspect_ratio) is used in the calculation of auto sizes and some other layout functions.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/aspect-ratio)
   */
  aspectRatio?: string;
  /**
   * The `backdrop-filter` CSS property lets you apply graphical effects such as blurring or color shifting to the area behind an element. Because it applies to everything *behind* the element, to see the effect the element or its background needs to be transparent or partially transparent.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/backdrop-filter)
   */
  backdropFilter?: string;
  /**
   * The `backface-visibility` CSS property sets whether the back face of an element is visible when turned towards the user.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/backface-visibility)
   */
  backfaceVisibility?: "visible" | "hidden";
  /**
   * The `background-attachment` CSS property sets whether a background image's position is fixed within the [viewport](https://developer.mozilla.org/en-US/docs/Glossary/Viewport), or scrolls with its containing block.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/background-attachment)
   */
  backgroundAttachment?: string;
  /**
   * The `background-blend-mode` CSS property sets how an element's background images should blend with each other and with the element's background color.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/background-blend-mode)
   */
  backgroundBlendMode?: string;
  /**
   * The `background-clip` CSS property sets whether an element's background extends underneath its border box, padding box, or content box.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/background-clip)
   */
  backgroundClip?: string;
  /**
   * The `background-color` CSS property sets the background color of an element.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/background-color)
   */
  backgroundColor?: string;
  /**
   * The `background-image` CSS property sets one or more background images on an element.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/background-image)
   */
  backgroundImage?: string;
  /**
   * The `background-origin` CSS property sets the background's origin: from the border start, inside the border, or inside the padding.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/background-origin)
   */
  backgroundOrigin?: string;
  /**
   * The `background-position-x` CSS property sets the initial horizontal position for each background image. The position is relative to the position layer set by [`background-origin`](https://developer.mozilla.org/en-US/docs/Web/CSS/background-origin).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/background-position-x)
   */
  backgroundPositionX?: string;
  /**
   * The `background-position-y` CSS property sets the initial vertical position for each background image. The position is relative to the position layer set by [`background-origin`](https://developer.mozilla.org/en-US/docs/Web/CSS/background-origin).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/background-position-y)
   */
  backgroundPositionY?: string;
  /**
   * The `background-repeat` CSS property sets how background images are repeated. A background image can be repeated along the horizontal and vertical axes, or not repeated at all.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/background-repeat)
   */
  backgroundRepeat?: string;
  /**
   * The `background-size` CSS property sets the size of the element's background image. The image can be left to its natural size, stretched, or constrained to fit the available space.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/background-size)
   */
  backgroundSize?: string;
  /**
   * The `baseline-shift` attribute allows repositioning of the dominant-baseline relative to the dominant-baseline of the parent text content element. The shifted object might be a sub- or superscript.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/baseline-shift)
   */
  baselineShift?: string;
  /**
   * The `block-size` CSS property defines the size of an element's block along the [block axis](https://developer.mozilla.org/en-US/docs/Glossary/Grid_Axis). If the [`writing-mode`](https://developer.mozilla.org/en-US/docs/Web/CSS/writing-mode) is horizontal, it corresponds to the [`height`](https://developer.mozilla.org/en-US/docs/Web/CSS/height); if the writing mode is vertical, it corresponds to the [`width`](https://developer.mozilla.org/en-US/docs/Web/CSS/width). A related property is [`inline-size`](https://developer.mozilla.org/en-US/docs/Web/CSS/inline-size), which defines the other dimension of the element.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/block-size)
   */
  blockSize?: string;
  /**
   * The `border-block-end-color` CSS property defines the color of the logical block-end border of an element, which maps to a physical border color depending on the element's writing mode, directionality, and text orientation. It corresponds to the [`border-top-color`](https://developer.mozilla.org/en-US/docs/Web/CSS/border-top-color), [`border-right-color`](https://developer.mozilla.org/en-US/docs/Web/CSS/border-right-color), [`border-bottom-color`](https://developer.mozilla.org/en-US/docs/Web/CSS/border-bottom-color), or [`border-left-color`](https://developer.mozilla.org/en-US/docs/Web/CSS/border-left-color) property depending on the values defined for [`writing-mode`](https://developer.mozilla.org/en-US/docs/Web/CSS/writing-mode), [`direction`](https://developer.mozilla.org/en-US/docs/Web/CSS/direction), and [`text-orientation`](https://developer.mozilla.org/en-US/docs/Web/CSS/text-orientation).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/border-block-end-color)
   */
  borderBlockEndColor?: string;
  /**
   * The `border-block-end-style` CSS property defines the style of the logical block-end border of an element, which maps to a physical border style depending on the element's writing mode, directionality, and text orientation. It corresponds to the [`border-top-style`](https://developer.mozilla.org/en-US/docs/Web/CSS/border-top-style), [`border-right-style`](https://developer.mozilla.org/en-US/docs/Web/CSS/border-right-style), [`border-bottom-style`](https://developer.mozilla.org/en-US/docs/Web/CSS/border-bottom-style), or [`border-left-style`](https://developer.mozilla.org/en-US/docs/Web/CSS/border-left-style) property depending on the values defined for [`writing-mode`](https://developer.mozilla.org/en-US/docs/Web/CSS/writing-mode), [`direction`](https://developer.mozilla.org/en-US/docs/Web/CSS/direction), and [`text-orientation`](https://developer.mozilla.org/en-US/docs/Web/CSS/text-orientation).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/border-block-end-style)
   */
  borderBlockEndStyle?:
    | "none"
    | "hidden"
    | "dotted"
    | "dashed"
    | "solid"
    | "double"
    | "groove"
    | "ridge"
    | "inset"
    | "outset";
  /**
   * The `border-block-end-width` CSS property defines the width of the logical block-end border of an element, which maps to a physical border width depending on the element's writing mode, directionality, and text orientation. It corresponds to the [`border-top-width`](https://developer.mozilla.org/en-US/docs/Web/CSS/border-top-width), [`border-right-width`](https://developer.mozilla.org/en-US/docs/Web/CSS/border-right-width), [`border-bottom-width`](https://developer.mozilla.org/en-US/docs/Web/CSS/border-bottom-width), or [`border-left-width`](https://developer.mozilla.org/en-US/docs/Web/CSS/border-left-width) property depending on the values defined for [`writing-mode`](https://developer.mozilla.org/en-US/docs/Web/CSS/writing-mode), [`direction`](https://developer.mozilla.org/en-US/docs/Web/CSS/direction), and [`text-orientation`](https://developer.mozilla.org/en-US/docs/Web/CSS/text-orientation).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/border-block-end-width)
   */
  borderBlockEndWidth?: string;
  /**
   * The `border-block-start-color` CSS property defines the color of the logical block-start border of an element, which maps to a physical border color depending on the element's writing mode, directionality, and text orientation. It corresponds to the [`border-top-color`](https://developer.mozilla.org/en-US/docs/Web/CSS/border-top-color), [`border-right-color`](https://developer.mozilla.org/en-US/docs/Web/CSS/border-right-color), [`border-bottom-color`](https://developer.mozilla.org/en-US/docs/Web/CSS/border-bottom-color), or [`border-left-color`](https://developer.mozilla.org/en-US/docs/Web/CSS/border-left-color) property depending on the values defined for [`writing-mode`](https://developer.mozilla.org/en-US/docs/Web/CSS/writing-mode), [`direction`](https://developer.mozilla.org/en-US/docs/Web/CSS/direction), and [`text-orientation`](https://developer.mozilla.org/en-US/docs/Web/CSS/text-orientation).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/border-block-start-color)
   */
  borderBlockStartColor?: string;
  /**
   * The `border-block-start-style` CSS property defines the style of the logical block start border of an element, which maps to a physical border style depending on the element's writing mode, directionality, and text orientation. It corresponds to the [`border-top-style`](https://developer.mozilla.org/en-US/docs/Web/CSS/border-top-style), [`border-right-style`](https://developer.mozilla.org/en-US/docs/Web/CSS/border-right-style), [`border-bottom-style`](https://developer.mozilla.org/en-US/docs/Web/CSS/border-bottom-style), or [`border-left-style`](https://developer.mozilla.org/en-US/docs/Web/CSS/border-left-style) property depending on the values defined for [`writing-mode`](https://developer.mozilla.org/en-US/docs/Web/CSS/writing-mode), [`direction`](https://developer.mozilla.org/en-US/docs/Web/CSS/direction), and [`text-orientation`](https://developer.mozilla.org/en-US/docs/Web/CSS/text-orientation).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/border-block-start-style)
   */
  borderBlockStartStyle?:
    | "none"
    | "hidden"
    | "dotted"
    | "dashed"
    | "solid"
    | "double"
    | "groove"
    | "ridge"
    | "inset"
    | "outset";
  /**
   * The `border-block-start-width` CSS property defines the width of the logical block-start border of an element, which maps to a physical border width depending on the element's writing mode, directionality, and text orientation. It corresponds to the [`border-top-width`](https://developer.mozilla.org/en-US/docs/Web/CSS/border-top-width), [`border-right-width`](https://developer.mozilla.org/en-US/docs/Web/CSS/border-right-width), [`border-bottom-width`](https://developer.mozilla.org/en-US/docs/Web/CSS/border-bottom-width), or [`border-left-width`](https://developer.mozilla.org/en-US/docs/Web/CSS/border-left-width) property depending on the values defined for [`writing-mode`](https://developer.mozilla.org/en-US/docs/Web/CSS/writing-mode), [`direction`](https://developer.mozilla.org/en-US/docs/Web/CSS/direction), and [`text-orientation`](https://developer.mozilla.org/en-US/docs/Web/CSS/text-orientation).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/border-block-start-width)
   */
  borderBlockStartWidth?: string;
  /**
   * The `border-bottom-color` CSS property sets the color of an element's bottom [border](https://developer.mozilla.org/en-US/docs/Web/CSS/border). It can also be set with the shorthand CSS properties [`border-color`](https://developer.mozilla.org/en-US/docs/Web/CSS/border-color) or [`border-bottom`](https://developer.mozilla.org/en-US/docs/Web/CSS/border-bottom).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/border-bottom-color)
   */
  borderBottomColor?: string;
  /**
   * The `border-bottom-left-radius` CSS property rounds the bottom-left corner of an element by specifying the radius (or the radius of the semi-major and semi-minor axes) of the ellipse defining the curvature of the corner.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/border-bottom-left-radius)
   */
  borderBottomLeftRadius?: string;
  /**
   * The `border-bottom-right-radius` CSS property rounds the bottom-right corner of an element by specifying the radius (or the radius of the semi-major and semi-minor axes) of the ellipse defining the curvature of the corner.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/border-bottom-right-radius)
   */
  borderBottomRightRadius?: string;
  /**
   * The `border-bottom-style` CSS property sets the line style of an element's bottom [`border`](https://developer.mozilla.org/en-US/docs/Web/CSS/border).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/border-bottom-style)
   */
  borderBottomStyle?:
    | "none"
    | "hidden"
    | "dotted"
    | "dashed"
    | "solid"
    | "double"
    | "groove"
    | "ridge"
    | "inset"
    | "outset";
  /**
   * The `border-bottom-width` CSS property sets the width of the bottom border of an element.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/border-bottom-width)
   */
  borderBottomWidth?: string;
  /**
   * The `border-collapse` CSS property sets whether cells inside a [`<table>`](https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Elements/table) have shared or separate borders.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/border-collapse)
   */
  borderCollapse?: "collapse" | "separate";
  /**
   * The `border-end-end-radius` CSS property defines a logical border radius on an element, which maps to a physical border radius that depends on the element's [`writing-mode`](https://developer.mozilla.org/en-US/docs/Web/CSS/writing-mode), [`direction`](https://developer.mozilla.org/en-US/docs/Web/CSS/direction), and [`text-orientation`](https://developer.mozilla.org/en-US/docs/Web/CSS/text-orientation). This is useful when building styles to work regardless of the [text orientation](https://developer.mozilla.org/en-US/docs/Web/CSS/text-orientation) and [writing mode](https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_writing_modes).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/border-end-end-radius)
   */
  borderEndEndRadius?: string;
  /**
   * The `border-end-start-radius` CSS property defines a logical border radius on an element, which maps to a physical border radius depending on the element's [`writing-mode`](https://developer.mozilla.org/en-US/docs/Web/CSS/writing-mode), [`direction`](https://developer.mozilla.org/en-US/docs/Web/CSS/direction), and [`text-orientation`](https://developer.mozilla.org/en-US/docs/Web/CSS/text-orientation). This is useful when building styles to work regardless of the [text orientation](https://developer.mozilla.org/en-US/docs/Web/CSS/text-orientation) and [writing mode](https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_writing_modes).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/border-end-start-radius)
   */
  borderEndStartRadius?: string;
  /**
   * The `border-image-outset` CSS property sets the distance by which an element's [border image](https://developer.mozilla.org/en-US/docs/Web/CSS/border-image) is set out from its border box.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/border-image-outset)
   */
  borderImageOutset?: string;
  /**
   * The `border-image-repeat` CSS property defines how the images for the sides and the middle part of the [border image](https://developer.mozilla.org/en-US/docs/Web/CSS/border-image) are scaled and tiled. The middle region can be displayed by using the keyword "fill" in the [`border-image-slice`](https://developer.mozilla.org/en-US/docs/Web/CSS/border-image-slice) property.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/border-image-repeat)
   */
  borderImageRepeat?: string;
  /**
   * The `border-image-slice` CSS property divides the image specified by [`border-image-source`](https://developer.mozilla.org/en-US/docs/Web/CSS/border-image-source) into regions. These regions form the components of an element's [border image](https://developer.mozilla.org/en-US/docs/Web/CSS/border-image).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/border-image-slice)
   */
  borderImageSlice?: string;
  /**
   * The `border-image-source` CSS property sets the source image used to create an element's [border image](https://developer.mozilla.org/en-US/docs/Web/CSS/border-image).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/border-image-source)
   */
  borderImageSource?: string;
  /**
   * The `border-image-width` CSS property sets the width of an element's [border image](https://developer.mozilla.org/en-US/docs/Web/CSS/border-image).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/border-image-width)
   */
  borderImageWidth?: string;
  /**
   * The `border-inline-end-color` CSS property defines the color of the logical inline-end border of an element, which maps to a physical border color depending on the element's writing mode, directionality, and text orientation. It corresponds to the [`border-top-color`](https://developer.mozilla.org/en-US/docs/Web/CSS/border-top-color), [`border-right-color`](https://developer.mozilla.org/en-US/docs/Web/CSS/border-right-color), [`border-bottom-color`](https://developer.mozilla.org/en-US/docs/Web/CSS/border-bottom-color), or [`border-left-color`](https://developer.mozilla.org/en-US/docs/Web/CSS/border-left-color) property depending on the values defined for [`writing-mode`](https://developer.mozilla.org/en-US/docs/Web/CSS/writing-mode), [`direction`](https://developer.mozilla.org/en-US/docs/Web/CSS/direction), and [`text-orientation`](https://developer.mozilla.org/en-US/docs/Web/CSS/text-orientation).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/border-inline-end-color)
   */
  borderInlineEndColor?: string;
  /**
   * The `border-inline-end-style` CSS property defines the style of the logical inline end border of an element, which maps to a physical border style depending on the element's writing mode, directionality, and text orientation. It corresponds to the [`border-top-style`](https://developer.mozilla.org/en-US/docs/Web/CSS/border-top-style), [`border-right-style`](https://developer.mozilla.org/en-US/docs/Web/CSS/border-right-style), [`border-bottom-style`](https://developer.mozilla.org/en-US/docs/Web/CSS/border-bottom-style), or [`border-left-style`](https://developer.mozilla.org/en-US/docs/Web/CSS/border-left-style) property depending on the values defined for [`writing-mode`](https://developer.mozilla.org/en-US/docs/Web/CSS/writing-mode), [`direction`](https://developer.mozilla.org/en-US/docs/Web/CSS/direction), and [`text-orientation`](https://developer.mozilla.org/en-US/docs/Web/CSS/text-orientation).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/border-inline-end-style)
   */
  borderInlineEndStyle?:
    | "none"
    | "hidden"
    | "dotted"
    | "dashed"
    | "solid"
    | "double"
    | "groove"
    | "ridge"
    | "inset"
    | "outset";
  /**
   * The `border-inline-end-width` CSS property defines the width of the logical inline-end border of an element, which maps to a physical border width depending on the element's writing mode, directionality, and text orientation. It corresponds to the [`border-top-width`](https://developer.mozilla.org/en-US/docs/Web/CSS/border-top-width), [`border-right-width`](https://developer.mozilla.org/en-US/docs/Web/CSS/border-right-width), [`border-bottom-width`](https://developer.mozilla.org/en-US/docs/Web/CSS/border-bottom-width), or [`border-left-width`](https://developer.mozilla.org/en-US/docs/Web/CSS/border-left-width) property depending on the values defined for [`writing-mode`](https://developer.mozilla.org/en-US/docs/Web/CSS/writing-mode), [`direction`](https://developer.mozilla.org/en-US/docs/Web/CSS/direction), and [`text-orientation`](https://developer.mozilla.org/en-US/docs/Web/CSS/text-orientation).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/border-inline-end-width)
   */
  borderInlineEndWidth?: string;
  /**
   * The `border-inline-start-color` CSS property defines the color of the logical inline start border of an element, which maps to a physical border color depending on the element's writing mode, directionality, and text orientation. It corresponds to the [`border-top-color`](https://developer.mozilla.org/en-US/docs/Web/CSS/border-top-color), [`border-right-color`](https://developer.mozilla.org/en-US/docs/Web/CSS/border-right-color), [`border-bottom-color`](https://developer.mozilla.org/en-US/docs/Web/CSS/border-bottom-color), or [`border-left-color`](https://developer.mozilla.org/en-US/docs/Web/CSS/border-left-color) property depending on the values defined for [`writing-mode`](https://developer.mozilla.org/en-US/docs/Web/CSS/writing-mode), [`direction`](https://developer.mozilla.org/en-US/docs/Web/CSS/direction), and [`text-orientation`](https://developer.mozilla.org/en-US/docs/Web/CSS/text-orientation).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/border-inline-start-color)
   */
  borderInlineStartColor?: string;
  /**
   * The `border-inline-start-style` CSS property defines the style of the logical inline start border of an element, which maps to a physical border style depending on the element's writing mode, directionality, and text orientation. It corresponds to the [`border-top-style`](https://developer.mozilla.org/en-US/docs/Web/CSS/border-top-style), [`border-right-style`](https://developer.mozilla.org/en-US/docs/Web/CSS/border-right-style), [`border-bottom-style`](https://developer.mozilla.org/en-US/docs/Web/CSS/border-bottom-style), or [`border-left-style`](https://developer.mozilla.org/en-US/docs/Web/CSS/border-left-style) property depending on the values defined for [`writing-mode`](https://developer.mozilla.org/en-US/docs/Web/CSS/writing-mode), [`direction`](https://developer.mozilla.org/en-US/docs/Web/CSS/direction), and [`text-orientation`](https://developer.mozilla.org/en-US/docs/Web/CSS/text-orientation).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/border-inline-start-style)
   */
  borderInlineStartStyle?:
    | "none"
    | "hidden"
    | "dotted"
    | "dashed"
    | "solid"
    | "double"
    | "groove"
    | "ridge"
    | "inset"
    | "outset";
  /**
   * The `border-inline-start-width` CSS property defines the width of the logical inline-start border of an element, which maps to a physical border width depending on the element's writing mode, directionality, and text orientation. It corresponds to the [`border-top-width`](https://developer.mozilla.org/en-US/docs/Web/CSS/border-top-width), [`border-right-width`](https://developer.mozilla.org/en-US/docs/Web/CSS/border-right-width), [`border-bottom-width`](https://developer.mozilla.org/en-US/docs/Web/CSS/border-bottom-width), or [`border-left-width`](https://developer.mozilla.org/en-US/docs/Web/CSS/border-left-width) property depending on the values defined for [`writing-mode`](https://developer.mozilla.org/en-US/docs/Web/CSS/writing-mode), [`direction`](https://developer.mozilla.org/en-US/docs/Web/CSS/direction), and [`text-orientation`](https://developer.mozilla.org/en-US/docs/Web/CSS/text-orientation).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/border-inline-start-width)
   */
  borderInlineStartWidth?: string;
  /**
   * The `border-left-color` CSS property sets the color of an element's left [border](https://developer.mozilla.org/en-US/docs/Web/CSS/border). It can also be set with the shorthand CSS properties [`border-color`](https://developer.mozilla.org/en-US/docs/Web/CSS/border-color) or [`border-left`](https://developer.mozilla.org/en-US/docs/Web/CSS/border-left).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/border-left-color)
   */
  borderLeftColor?: string;
  /**
   * The `border-left-style` CSS property sets the line style of an element's left [`border`](https://developer.mozilla.org/en-US/docs/Web/CSS/border).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/border-left-style)
   */
  borderLeftStyle?:
    | "none"
    | "hidden"
    | "dotted"
    | "dashed"
    | "solid"
    | "double"
    | "groove"
    | "ridge"
    | "inset"
    | "outset";
  /**
   * The `border-left-width` CSS property sets the width of the left border of an element.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/border-left-width)
   */
  borderLeftWidth?: string;
  /**
   * The `border-right-color` CSS property sets the color of an element's right [border](https://developer.mozilla.org/en-US/docs/Web/CSS/border). It can also be set with the shorthand CSS properties [`border-color`](https://developer.mozilla.org/en-US/docs/Web/CSS/border-color) or [`border-right`](https://developer.mozilla.org/en-US/docs/Web/CSS/border-right).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/border-right-color)
   */
  borderRightColor?: string;
  /**
   * The `border-right-style` CSS property sets the line style of an element's right [`border`](https://developer.mozilla.org/en-US/docs/Web/CSS/border).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/border-right-style)
   */
  borderRightStyle?:
    | "none"
    | "hidden"
    | "dotted"
    | "dashed"
    | "solid"
    | "double"
    | "groove"
    | "ridge"
    | "inset"
    | "outset";
  /**
   * The `border-right-width` CSS property sets the width of the right border of an element.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/border-right-width)
   */
  borderRightWidth?: string;
  /**
   * The `border-spacing` CSS property sets the distance between the borders of adjacent cells in a [`<table>`](https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Elements/table). This property applies only when [`border-collapse`](https://developer.mozilla.org/en-US/docs/Web/CSS/border-collapse) is `separate`.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/border-spacing)
   */
  borderSpacing?: string;
  /**
   * The `border-start-end-radius` CSS property defines a logical border radius on an element, which maps to a physical border radius depending on the element's [`writing-mode`](https://developer.mozilla.org/en-US/docs/Web/CSS/writing-mode), [`direction`](https://developer.mozilla.org/en-US/docs/Web/CSS/direction), and [`text-orientation`](https://developer.mozilla.org/en-US/docs/Web/CSS/text-orientation). This is useful when building styles to work regardless of the [text orientation](https://developer.mozilla.org/en-US/docs/Web/CSS/text-orientation) and [writing mode](https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_writing_modes).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/border-start-end-radius)
   */
  borderStartEndRadius?: string;
  /**
   * The `border-start-start-radius` CSS property defines a logical border radius on an element, which maps to a physical border radius that depends on the element's [`writing-mode`](https://developer.mozilla.org/en-US/docs/Web/CSS/writing-mode), [`direction`](https://developer.mozilla.org/en-US/docs/Web/CSS/direction), and [`text-orientation`](https://developer.mozilla.org/en-US/docs/Web/CSS/text-orientation). This is useful when building styles to work regardless of the [text orientation](https://developer.mozilla.org/en-US/docs/Web/CSS/text-orientation) and [writing mode](https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_writing_modes).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/border-start-start-radius)
   */
  borderStartStartRadius?: string;
  /**
   * The `border-top-color` CSS property sets the color of an element's top [border](https://developer.mozilla.org/en-US/docs/Web/CSS/border). It can also be set with the shorthand CSS properties [`border-color`](https://developer.mozilla.org/en-US/docs/Web/CSS/border-color) or [`border-top`](https://developer.mozilla.org/en-US/docs/Web/CSS/border-top).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/border-top-color)
   */
  borderTopColor?: string;
  /**
   * The `border-top-left-radius` CSS property rounds the top-left corner of an element by specifying the radius (or the radius of the semi-major and semi-minor axes) of the ellipse defining the curvature of the corner.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/border-top-left-radius)
   */
  borderTopLeftRadius?: string;
  /**
   * The `border-top-right-radius` CSS property rounds the top-right corner of an element by specifying the radius (or the radius of the semi-major and semi-minor axes) of the ellipse defining the curvature of the corner.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/border-top-right-radius)
   */
  borderTopRightRadius?: string;
  /**
   * The `border-top-style` CSS property sets the line style of an element's top [`border`](https://developer.mozilla.org/en-US/docs/Web/CSS/border).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/border-top-style)
   */
  borderTopStyle?:
    | "none"
    | "hidden"
    | "dotted"
    | "dashed"
    | "solid"
    | "double"
    | "groove"
    | "ridge"
    | "inset"
    | "outset";
  /**
   * The `border-top-width` CSS property sets the width of the top border of an element.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/border-top-width)
   */
  borderTopWidth?: string;
  /**
   * The `bottom` CSS property participates in setting the vertical position of a [positioned element](https://developer.mozilla.org/en-US/docs/Web/CSS/position). This [inset property](https://developer.mozilla.org/en-US/docs/Glossary/Inset_properties) has no effect on non-positioned elements.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/bottom)
   */
  bottom?: string;
  /**
   * The `box-decoration-break` CSS property specifies how an element's [fragments](https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_fragmentation) should be rendered when broken across multiple lines, columns, or pages.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/box-decoration-break)
   */
  boxDecorationBreak?: "slice" | "clone";
  /**
   * The `box-shadow` CSS property adds shadow effects around an element's frame. You can set multiple effects separated by commas. A box shadow is described by X and Y offsets relative to the element, blur and spread radius, and color.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/box-shadow)
   */
  boxShadow?: string;
  /**
   * The `box-sizing` CSS property sets how the total width and height of an element is calculated.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/box-sizing)
   */
  boxSizing?: "content-box" | "border-box";
  /**
   * The `break-after` CSS property sets how page, column, or region breaks should behave after a generated box. If there is no generated box, the property is ignored.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/break-after)
   */
  breakAfter?:
    | "auto"
    | "avoid"
    | "always"
    | "all"
    | "avoid-page"
    | "page"
    | "left"
    | "right"
    | "recto"
    | "verso"
    | "avoid-column"
    | "column"
    | "avoid-region"
    | "region";
  /**
   * The `break-before` CSS property sets how page, column, or region breaks should behave before a generated box. If there is no generated box, the property is ignored.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/break-before)
   */
  breakBefore?:
    | "auto"
    | "avoid"
    | "always"
    | "all"
    | "avoid-page"
    | "page"
    | "left"
    | "right"
    | "recto"
    | "verso"
    | "avoid-column"
    | "column"
    | "avoid-region"
    | "region";
  /**
   * The `break-inside` CSS property sets how page, column, or region breaks should behave inside a generated box. If there is no generated box, the property is ignored.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/break-inside)
   */
  breakInside?:
    | "auto"
    | "avoid"
    | "avoid-page"
    | "avoid-column"
    | "avoid-region";
  /**
   * The `caption-side` CSS property puts the content of a table's [`<caption>`](https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Elements/caption) on the specified side. The values are relative to the [`writing-mode`](https://developer.mozilla.org/en-US/docs/Web/CSS/writing-mode) of the table.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/caption-side)
   */
  captionSide?:
    | "top"
    | "bottom"
    | "block-start"
    | "block-end"
    | "inline-start"
    | "inline-end";
  /**
   * The `caret-color` CSS property sets the color of the **insertion caret**, the visible marker where the next character typed will be inserted. This is sometimes referred to as the **text input cursor**. The caret appears in elements such as [`<input>`](https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Elements/input) or those with the [`contenteditable`](https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Global_attributes/contenteditable) attribute. The caret is typically a thin vertical line that flashes to help make it more noticeable. By default, it is black, but its color can be altered with this property.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/caret-color)
   */
  caretColor?: string;
  /**
   * The `clear` CSS property sets whether an element must be moved below (cleared) [floating](https://developer.mozilla.org/en-US/docs/Web/CSS/float) elements that precede it. The `clear` property applies to floating and non-floating elements.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/clear)
   */
  clear?: "none" | "left" | "right" | "both" | "inline-start" | "inline-end";
  /**
   * @deprecated This feature is no longer recommended. Though some browsers might still support it, it may have already been removed from the relevant web standards, may be in the process of being dropped, or may only be kept for compatibility purposes. Avoid using it, and update existing code if possible; see the [compatibility table](#browser_compatibility) at the bottom of this page to guide your decision. Be aware that this feature may cease to work at any time.
   *
   * **Warning:** Authors are encouraged to use the [`clip-path`](https://developer.mozilla.org/en-US/docs/Web/CSS/clip-path) property instead.
   *
   * The `clip` CSS property defines a visible portion of an element. The `clip` property applies only to absolutely positioned elements — that is, elements with [`position: absolute`](https://developer.mozilla.org/en-US/docs/Web/CSS/position) or [`position: fixed`](https://developer.mozilla.org/en-US/docs/Web/CSS/position).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/clip)
   */
  clip?: string;
  /**
   * The `clip-path` CSS property creates a clipping region that sets what part of an element should be shown. Parts that are inside the region are shown, while those outside are hidden.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/clip-path)
   */
  clipPath?: string;
  /**
   * The `clip-rule` attribute only applies to graphics elements that are contained within a [`<clipPath>`](https://developer.mozilla.org/en-US/docs/Web/SVG/Reference/Element/clipPath) element. The `clip-rule` attribute basically works as the [`fill-rule`](https://developer.mozilla.org/en-US/docs/Web/SVG/Reference/Attribute/fill-rule) attribute, except that it applies to [`<clipPath>`](https://developer.mozilla.org/en-US/docs/Web/SVG/Reference/Element/clipPath) definitions.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/clip-rule)
   */
  clipRule?: "nonzero" | "evenodd";
  /**
   * The `color` CSS property sets the foreground [color value](https://developer.mozilla.org/en-US/docs/Web/CSS/color_value) of an element's text and [text decorations](https://developer.mozilla.org/en-US/docs/Web/CSS/text-decoration), and sets the [`currentcolor`](https://developer.mozilla.org/en-US/docs/Web/CSS/color_value#currentcolor_keyword) value. `currentcolor` may be used as an indirect value on *other* properties and is the default for other color properties, such as [`border-color`](https://developer.mozilla.org/en-US/docs/Web/CSS/border-color).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/color)
   */
  color?: string;
  /**
   * The `color-interpolation` attribute specifies the color space for gradient interpolations, color animations, and alpha compositing.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/color-interpolation)
   */
  colorInterpolation?: "auto" | "sRGB" | "linearRGB";
  /**
   * The `color-interpolation-filters` attribute specifies the color space for imaging operations performed via filter effects.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/color-interpolation-filters)
   */
  colorInterpolationFilters?: "auto" | "sRGB" | "linearRGB";
  /**
   * The `color-scheme` CSS property allows an element to indicate which color schemes it can comfortably be rendered in. User agents change the following aspects of the UI chrome to match the used color scheme:
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/color-scheme)
   */
  colorScheme?: string;
  /**
   * The `column-count` CSS property breaks an element's content into the specified number of columns.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/column-count)
   */
  columnCount?: string;
  /**
   * The `column-fill` CSS property controls how an element's contents are balanced when broken into columns.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/column-fill)
   */
  columnFill?: "auto" | "balance" | "balance-all";
  /**
   * The `column-gap` CSS property sets the size of the gap ([gutter](https://developer.mozilla.org/en-US/docs/Glossary/Gutters)) between an element's columns.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/column-gap)
   */
  columnGap?: string;
  /**
   * The `column-rule-color` CSS property sets the color of the line drawn between columns in a multi-column layout.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/column-rule-color)
   */
  columnRuleColor?: string;
  /**
   * The `column-rule-style` CSS property sets the style of the line drawn between columns in a multi-column layout.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/column-rule-style)
   */
  columnRuleStyle?:
    | "none"
    | "hidden"
    | "dotted"
    | "dashed"
    | "solid"
    | "double"
    | "groove"
    | "ridge"
    | "inset"
    | "outset";
  /**
   * The `column-rule-width` CSS property sets the width of the line drawn between columns in a multi-column layout.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/column-rule-width)
   */
  columnRuleWidth?: string;
  /**
   * The `column-span` CSS property makes it possible for an element to span across all columns when its value is set to `all`.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/column-span)
   */
  columnSpan?: "none" | "all";
  /**
   * The `column-width` CSS property sets the ideal column width in a multi-column layout. The container will have as many columns as can fit without any of them having a width less than the `column-width` value. If the width of the container is narrower than the specified value, the single column's width will be smaller than the declared column width.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/column-width)
   */
  columnWidth?: string;
  /**
   * The `contain` CSS property indicates that an element and its contents are, as much as possible, independent from the rest of the document tree. Containment enables isolating a subsection of the DOM, providing performance benefits by limiting calculations of layout, style, paint, size, or any combination to a DOM subtree rather than the entire page. Containment can also be used to scope CSS counters and quotes.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/contain)
   */
  contain?: string;
  /**
   * The `content` CSS property replaces content with a generated value. It can be used to define what is rendered inside an element or pseudo-element. For elements, the `content` property specifies whether the element renders normally (`normal` or `none`) or is replaced with an image (and associated "alt" text). For pseudo-elements and margin boxes, `content` defines the content as images, text, both, or none, which determines whether the element renders at all.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/content)
   */
  content?: string;
  /**
   * The `content-visibility` CSS property controls whether or not an element renders its contents at all, along with forcing a strong set of containments, allowing user agents to potentially omit large swathes of layout and rendering work until it becomes needed. It enables the user agent to skip an element's rendering work (including layout and painting) until it is needed — which makes the initial page load much faster.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/content-visibility)
   */
  contentVisibility?: "visible" | "hidden" | "auto";
  /**
   * The `counter-increment` CSS property can be used to increase or decrease the value of the named [CSS counters](https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_counter_styles/Using_CSS_counters) by the specified values, or to prevent all counters or an individual counter's value from being changed.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/counter-increment)
   */
  counterIncrement?: string;
  /**
   * The `counter-reset` CSS property creates named [CSS counters](https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_counter_styles/Using_CSS_counters) and initializes them to a specific value. It supports creating counters that count up from one to the number of elements, as well as those that count down from the number of elements to one.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/counter-reset)
   */
  counterReset?: string;
  /**
   * The `counter-set` CSS property sets [CSS counters](https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_counter_styles/Using_CSS_counters) on the element to the given values.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/counter-set)
   */
  counterSet?: string;
  /**
   * The `cursor` CSS property sets the mouse cursor, if any, to show when the mouse pointer is over an element.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/cursor)
   */
  cursor?: string;
  /**
   * The `cx` attribute defines the x-axis coordinate of a center point.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/cx)
   */
  cx?: string;
  /**
   * The `cy` attribute defines the y-axis coordinate of a center point.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/cy)
   */
  cy?: string;
  /**
   * The `d` attribute defines a path to be drawn.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/d)
   */
  d?: string;
  /**
   * **Warning:** Where possible, authors are encouraged to avoid using the `direction` CSS property and use the HTML [`dir`](https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Global_attributes/dir) global attribute instead.
   *
   * The `direction` CSS property sets the direction of text, table and grid columns, and horizontal overflow. Use `rtl` for languages written from right to left (like Hebrew or Arabic), and `ltr` for those written from left to right (like English and most other languages).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/direction)
   */
  direction?: "ltr" | "rtl";
  /**
   * The `display` CSS property sets whether an element is treated as a [block or inline box](https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_display/Flow_layout) and the layout used for its children, such as [flow layout](https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_display/Flow_layout), [grid](https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_grid_layout) or [flex](https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_flexible_box_layout).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/display)
   */
  display?:
    | "none"
    | "contents"
    | "block"
    | "inline"
    | "inline-block"
    | "inline-table"
    | "inline-flex"
    | "inline-grid"
    | "flow"
    | "flow-root"
    | "flex"
    | "grid"
    | "table"
    | "table-row-group"
    | "table-header-group"
    | "table-footer-group"
    | "table-row"
    | "table-cell"
    | "table-column-group"
    | "table-column"
    | "table-caption"
    | "list-item";
  /**
   * The `dominant-baseline` attribute specifies the dominant baseline, which is the baseline used to align the box's text and inline-level contents. It also indicates the default alignment baseline of any boxes participating in baseline alignment in the box's alignment context.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/dominant-baseline)
   */
  dominantBaseline?:
    | "auto"
    | "text-bottom"
    | "alphabetic"
    | "ideographic"
    | "middle"
    | "central"
    | "mathematical"
    | "hanging"
    | "text-top";
  /**
   * The `empty-cells` CSS property sets whether borders and backgrounds appear around [`<table>`](https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Elements/table) cells that have no visible content.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/empty-cells)
   */
  emptyCells?: "show" | "hide";
  /**
   * The `fill` attribute has two different meanings. For shapes and text it's a presentation attribute that defines the color (*or any SVG paint servers like gradients or patterns*) used to paint the element; for animation it defines the final state of the animation.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/fill)
   */
  fill?: string;
  /**
   * The `fill-opacity` attribute is a presentation attribute defining the opacity of the paint server (*color*, *gradient*, *pattern*, etc.) applied to a shape.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/fill-opacity)
   */
  fillOpacity?: string;
  /**
   * The `fill-rule` attribute is a presentation attribute defining the algorithm to use to determine the *inside* part of a shape.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/fill-rule)
   */
  fillRule?: "nonzero" | "evenodd";
  /**
   * The `field-sizing` CSS property defines how form elements (such as inputs) determine their size.
   *
   * The `content` value makes the element automatically size itself based on its content,
   * instead of using a fixed or default size.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/field-sizing)
   */
  fieldSizing?: "fixed" | "content";
  /**
   * The `filter` CSS property applies graphical effects like blur or color shift to an element. Filters are commonly used to adjust the rendering of images, backgrounds, and borders.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/filter)
   */
  filter?: string;
  /**
   * The `flex-basis` CSS property sets the initial main size of a [flex item](https://developer.mozilla.org/en-US/docs/Glossary/Flex_Item). It sets the size of the content box unless otherwise set with [`box-sizing`](https://developer.mozilla.org/en-US/docs/Web/CSS/box-sizing).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/flex-basis)
   */
  flexBasis?: string;
  /**
   * The `flex-direction` CSS property sets how flex items are placed in the flex container defining the main axis and the direction (normal or reversed).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/flex-direction)
   */
  flexDirection?: "row" | "row-reverse" | "column" | "column-reverse";
  /**
   * The `flex-grow` CSS property sets the flex grow factor, which specifies how much of the flex container's [**positive free space**](https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_flexible_box_layout/Controlling_ratios_of_flex_items_along_the_main_axis), if any, should be assigned to the flex item's [main size](https://developer.mozilla.org/en-US/docs/Learn_web_development/Core/CSS_layout/Flexbox#the_flex_model).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/flex-grow)
   */
  flexGrow?: string;
  /**
   * The `flex-shrink` CSS property sets the flex shrink factor of a flex item. If the size of all flex items is larger than the flex container, the [flex items can shrink](https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_flexible_box_layout/Controlling_ratios_of_flex_items_along_the_main_axis#the_flex-shrink_property) to fit according to their `flex-shrink` value. Each flex line's [negative free space](https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_flexible_box_layout/Controlling_ratios_of_flex_items_along_the_main_axis#positive_and_negative_free_space) is distributed between the line's flex items that have a `flex-shrink` value greater than `0`.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/flex-shrink)
   */
  flexShrink?: string;
  /**
   * The `flex-wrap` CSS property sets whether flex items are forced onto one line or can wrap onto multiple lines. If wrapping is allowed, it sets the direction that lines are stacked.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/flex-wrap)
   */
  flexWrap?: "nowrap" | "wrap" | "wrap-reverse";
  /**
   * The `float` CSS property places an element on the left or right side of its container, allowing text and inline elements to wrap around it. The element is removed from the normal flow of the page, though still remaining a part of the flow (in contrast to [absolute positioning](https://developer.mozilla.org/en-US/docs/Web/CSS/position#absolute_positioning)).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/float)
   */
  float?: "none" | "left" | "right" | "inline-start" | "inline-end";
  /**
   * The `flood-color` attribute indicates what color to use to flood the current filter primitive subregion.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/flood-color)
   */
  floodColor?: string;
  /**
   * The `flood-opacity` attribute indicates the opacity value to use across the current filter primitive subregion.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/flood-opacity)
   */
  floodOpacity?: string;
  /**
   * The `font-family` CSS property specifies a prioritized list of one or more font family names and/or generic family names for the selected element.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/font-family)
   */
  fontFamily?: string;
  /**
   * The `font-feature-settings` CSS property controls advanced typographic features in OpenType fonts.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/font-feature-settings)
   */
  fontFeatureSettings?: string;
  /**
   * The `font-kerning` CSS property sets the use of the kerning information stored in a font.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/font-kerning)
   */
  fontKerning?: "auto" | "normal" | "none";
  /**
   * The `font-language-override` CSS property controls the use of language-specific glyphs in a typeface.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/font-language-override)
   */
  fontLanguageOverride?: string;
  /**
   * The `font-optical-sizing` CSS property sets whether text rendering is optimized for viewing at different sizes.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/font-optical-sizing)
   */
  fontOpticalSizing?: "auto" | "none";
  /**
   * The `font-size` CSS property sets the size of the font. Changing the font size also updates the sizes of the font size-relative [`<length>`](https://developer.mozilla.org/en-US/docs/Web/CSS/length) units, such as `em`, `ex`, and so forth.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/font-size)
   */
  fontSize?: string;
  /**
   * The `font-size-adjust` CSS property provides a way to modify the size of lowercase letters relative to the size of uppercase letters, which defines the overall [`font-size`](https://developer.mozilla.org/en-US/docs/Web/CSS/font-size). This property is useful for situations where font fallback can occur.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/font-size-adjust)
   */
  fontSizeAdjust?: string;
  /**
   * @deprecated This feature is no longer recommended. Though some browsers might still support it, it may have already been removed from the relevant web standards, may be in the process of being dropped, or may only be kept for compatibility purposes. Avoid using it, and update existing code if possible; see the [compatibility table](#browser_compatibility) at the bottom of this page to guide your decision. Be aware that this feature may cease to work at any time.
   *
   * **Note:** The `font-stretch` property [has now been renamed to `font-width`](https://drafts.csswg.org/css-fonts/#font-stretch-desc) in the specifications. The name `font-stretch` has been kept as an alias for the font-width property. The new name `font-width` is not yet supported by any browsers.
   *
   * The `font-stretch` CSS property selects a normal, condensed, or expanded face from a font.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/font-stretch)
   */
  fontStretch?: string;
  /**
   * The `font-style` CSS property sets whether a font should be styled with a normal, italic, or oblique face from its [`font-family`](https://developer.mozilla.org/en-US/docs/Web/CSS/font-family).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/font-style)
   */
  fontStyle?: "normal" | "italic" | "oblique";
  /**
   * The `font-synthesis-small-caps` CSS property lets you specify whether or not the browser may synthesize small-caps typeface when it is missing in a font family. Small-caps glyphs typically use the form of uppercase letters but are reduced to the size of lowercase letters.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/font-synthesis-small-caps)
   */
  fontSynthesisSmallCaps?: "auto" | "none";
  /**
   * The `font-synthesis-style` CSS property lets you specify whether or not the browser may synthesize the oblique typeface when it is missing in a font family.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/font-synthesis-style)
   */
  fontSynthesisStyle?: "auto" | "none";
  /**
   * The `font-synthesis-weight` CSS property lets you specify whether or not the browser may synthesize the bold typeface when it is missing in a font family.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/font-synthesis-weight)
   */
  fontSynthesisWeight?: "auto" | "none";
  /**
   * The `font-variant-alternates` CSS property controls the usage of alternate glyphs. These alternate glyphs may be referenced by alternative names defined in [`\@font-feature-values`](https://developer.mozilla.org/en-US/docs/Web/CSS/\@font-feature-values).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/font-variant-alternates)
   */
  fontVariantAlternates?: string;
  /**
   * The `font-variant-caps` CSS property controls the use of alternate glyphs used for small or petite capitals or for titling.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/font-variant-caps)
   */
  fontVariantCaps?:
    | "normal"
    | "small-caps"
    | "all-small-caps"
    | "petite-caps"
    | "all-petite-caps"
    | "unicase"
    | "titling-caps";
  /**
   * The `font-variant-east-asian` CSS property controls the use of alternate glyphs for East Asian scripts, like Japanese and Chinese.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/font-variant-east-asian)
   */
  fontVariantEastAsian?: string;
  /**
   * The `font-variant-emoji` CSS property specifies the default presentation style for displaying emojis.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/font-variant-emoji)
   */
  fontVariantEmoji?: "normal" | "text" | "emoji" | "unicode";
  /**
   * The `font-variant-ligatures` CSS property controls which [ligatures](https://developer.mozilla.org/en-US/docs/Glossary/Ligature) and contextual forms are used in the textual content of the elements it applies to. This leads to more harmonized forms in the resulting text.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/font-variant-ligatures)
   */
  fontVariantLigatures?: string;
  /**
   * The `font-variant-numeric` CSS property controls the usage of alternate glyphs for numbers, fractions, and ordinal markers.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/font-variant-numeric)
   */
  fontVariantNumeric?: string;
  /**
   * The `font-variant-position` CSS property controls the use of alternate, smaller glyphs that are positioned as superscript or subscript.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/font-variant-position)
   */
  fontVariantPosition?: "normal" | "sub" | "super";
  /**
   * The `font-variation-settings` CSS property provides low-level control over [variable font](https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_fonts/Variable_fonts_guide) characteristics by letting you specify the four letter axis names of the characteristics you want to vary along with their values.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/font-variation-settings)
   */
  fontVariationSettings?: string;
  /**
   * The `font-weight` CSS property sets the weight (or boldness) of the font. The weights available depend on the [`font-family`](https://developer.mozilla.org/en-US/docs/Web/CSS/font-family) that is currently set.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/font-weight)
   */
  fontWeight?: string;
  /**
   * The `forced-color-adjust` CSS property allows authors to opt certain elements out of forced colors mode. This then restores the control of those values to CSS.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/forced-color-adjust)
   */
  forcedColorAdjust?: "auto" | "none";
  /**
   * @deprecated This feature is no longer recommended. Though some browsers might still support it, it may have already been removed from the relevant web standards, may be in the process of being dropped, or may only be kept for compatibility purposes. Avoid using it, and update existing code if possible; see the [compatibility table](#browser_compatibility) at the bottom of this page to guide your decision. Be aware that this feature may cease to work at any time.
   *
   * The `glyph-orientation-horizontal` attribute affects the amount that the current text position advances as each glyph is rendered.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/glyph-orientation-horizontal)
   */
  glyphOrientationHorizontal?: string;
  /**
   * @deprecated This feature is no longer recommended. Though some browsers might still support it, it may have already been removed from the relevant web standards, may be in the process of being dropped, or may only be kept for compatibility purposes. Avoid using it, and update existing code if possible; see the [compatibility table](#browser_compatibility) at the bottom of this page to guide your decision. Be aware that this feature may cease to work at any time.
   *
   * The `glyph-orientation-vertical` attribute affects the amount that the current text position advances as each glyph is rendered.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/glyph-orientation-vertical)
   */
  glyphOrientationVertical?: string;
  /**
   * The `grid-auto-columns` CSS property specifies the size of an implicitly-created grid column [track](https://developer.mozilla.org/en-US/docs/Glossary/Grid_Tracks) or pattern of tracks.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/grid-auto-columns)
   */
  gridAutoColumns?: string;
  /**
   * The `grid-auto-flow` CSS property controls how the auto-placement algorithm works, specifying exactly how auto-placed items get flowed into the grid.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/grid-auto-flow)
   */
  gridAutoFlow?: "row" | "column" | "dense" | "row dense" | "column dense";
  /**
   * The `grid-auto-rows` CSS property specifies the size of an implicitly-created grid row [track](https://developer.mozilla.org/en-US/docs/Glossary/Grid_Tracks) or pattern of tracks.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/grid-auto-rows)
   */
  gridAutoRows?: string;
  /**
   * The `grid-column-end` CSS property specifies a grid item's end position within the grid column by contributing a line, a span, or nothing (automatic) to its grid placement, thereby specifying the block-end edge of its [grid area](https://developer.mozilla.org/en-US/docs/Glossary/Grid_Areas).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/grid-column-end)
   */
  gridColumnEnd?: string;
  /**
   * The `grid-column-start` CSS property specifies a grid item's start position within the grid column by contributing a line, a span, or nothing (automatic) to its grid placement. This start position defines the block-start edge of the [grid area](https://developer.mozilla.org/en-US/docs/Glossary/Grid_Areas).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/grid-column-start)
   */
  gridColumnStart?: string;
  /**
   * The `grid-row-end` CSS property specifies a grid item's end position within the grid row by contributing a line, a span, or nothing (automatic) to its grid placement, thereby specifying the inline-end edge of its [grid area](https://developer.mozilla.org/en-US/docs/Glossary/Grid_Areas).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/grid-row-end)
   */
  gridRowEnd?: string;
  /**
   * The `grid-row-start` CSS property specifies a grid item's start position within the grid row by contributing a line, a span, or nothing (automatic) to its grid placement, thereby specifying the inline-start edge of its [grid area](https://developer.mozilla.org/en-US/docs/Glossary/Grid_Areas).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/grid-row-start)
   */
  gridRowStart?: string;
  /**
   * The `grid-template-areas` CSS property specifies named [grid areas](https://developer.mozilla.org/en-US/docs/Glossary/Grid_Areas), establishing the cells in the grid and assigning them names.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/grid-template-areas)
   */
  gridTemplateAreas?: string;
  /**
   * The `grid-template-columns` CSS property defines the line names and track sizing functions of the [grid columns](https://developer.mozilla.org/en-US/docs/Glossary/Grid_Column).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/grid-template-columns)
   */
  gridTemplateColumns?: string;
  /**
   * The `grid-template-rows` CSS property defines the line names and track sizing functions of the [grid rows](https://developer.mozilla.org/en-US/docs/Glossary/Grid_Row).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/grid-template-rows)
   */
  gridTemplateRows?: string;
  /**
   * The `hanging-punctuation` CSS property specifies whether a punctuation mark should hang at the start or end of a line of text. Hanging punctuation may be placed outside the line box.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/hanging-punctuation)
   */
  hangingPunctuation?:
    | "none"
    | "first"
    | "last"
    | "force-end"
    | "allow-end"
    | "first last"
    | "first force-end"
    | "first allow-end"
    | "last force-end"
    | "last allow-end"
    | "first force-end last"
    | "first allow-end last";
  /**
   * The `height` CSS property specifies the height of an element. By default, the property defines the height of the [content area](https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_box_model/Introduction_to_the_CSS_box_model#content_area). If [`box-sizing`](https://developer.mozilla.org/en-US/docs/Web/CSS/box-sizing) is set to `border-box`, however, it instead determines the height of the [border area](https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_box_model/Introduction_to_the_CSS_box_model#border_area).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/height)
   */
  height?: string;
  /**
   * The `hyphens` CSS property specifies how words should be hyphenated when text wraps across multiple lines. It can prevent hyphenation entirely, hyphenate at manually-specified points within the text, or let the browser automatically insert hyphens where appropriate.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/hyphens)
   */
  hyphens?: "none" | "manual" | "auto";
  /**
   * The `image-orientation` CSS property specifies a layout-independent correction to the orientation of an image.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/image-orientation)
   */
  imageOrientation?: "from-image" | "none";
  /**
   * The `image-rendering` CSS property sets an image scaling algorithm. The property applies to an element itself, to any images set in its other properties, and to its descendants.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/image-rendering)
   */
  imageRendering?: "auto" | "crisp-edges" | "pixelated";
  /**
   * **Experimental:** This is an [experimental technology](https://developer.mozilla.org/en-US/docs/MDN/Writing_guidelines/Experimental_deprecated_obsolete#experimental) Check the [Browser compatibility table](#browser_compatibility) carefully before using this in production.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/image-resolution)
   */
  imageResolution?: string;
  /**
   * The `initial-letter` CSS property sets the size and sink for dropped, raised, and sunken initial letters. This property applies to [`::first-letter`](https://developer.mozilla.org/en-US/docs/Web/CSS/::first-letter) pseudo-elements and inline-level first children of block containers.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/initial-letter)
   */
  initialLetter?: string;
  /**
   * The `inline-size` CSS property defines the size of an element's block along the [inline axis](https://developer.mozilla.org/en-US/docs/Glossary/Grid_Axis). If the [`writing-mode`](https://developer.mozilla.org/en-US/docs/Web/CSS/writing-mode) is horizontal, it corresponds to the [`width`](https://developer.mozilla.org/en-US/docs/Web/CSS/width); if the writing mode is vertical, it corresponds to the [`height`](https://developer.mozilla.org/en-US/docs/Web/CSS/height). A related property is [`block-size`](https://developer.mozilla.org/en-US/docs/Web/CSS/block-size), which defines the other dimension of the element.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/inline-size)
   */
  inlineSize?: string;
  /**
   * The `inset-block-end` CSS property defines the logical block end offset of an element, which maps to a physical inset depending on the element's writing mode, directionality, and text orientation. It corresponds to the [`top`](https://developer.mozilla.org/en-US/docs/Web/CSS/top), [`right`](https://developer.mozilla.org/en-US/docs/Web/CSS/right), [`bottom`](https://developer.mozilla.org/en-US/docs/Web/CSS/bottom), or [`left`](https://developer.mozilla.org/en-US/docs/Web/CSS/left) property depending on the values defined for [`writing-mode`](https://developer.mozilla.org/en-US/docs/Web/CSS/writing-mode), [`direction`](https://developer.mozilla.org/en-US/docs/Web/CSS/direction), and [`text-orientation`](https://developer.mozilla.org/en-US/docs/Web/CSS/text-orientation).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/inset-block-end)
   */
  insetBlockEnd?: string;
  /**
   * The `inset-block-start` CSS property defines the logical block start offset of an element, which maps to a physical inset depending on the element's writing mode, directionality, and text orientation. It corresponds to the [`top`](https://developer.mozilla.org/en-US/docs/Web/CSS/top), [`right`](https://developer.mozilla.org/en-US/docs/Web/CSS/right), [`bottom`](https://developer.mozilla.org/en-US/docs/Web/CSS/bottom), or [`left`](https://developer.mozilla.org/en-US/docs/Web/CSS/left) property depending on the values defined for [`writing-mode`](https://developer.mozilla.org/en-US/docs/Web/CSS/writing-mode), [`direction`](https://developer.mozilla.org/en-US/docs/Web/CSS/direction), and [`text-orientation`](https://developer.mozilla.org/en-US/docs/Web/CSS/text-orientation).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/inset-block-start)
   */
  insetBlockStart?: string;
  /**
   * The `inset-inline-end` CSS property defines the logical inline end inset of an element, which maps to a physical offset depending on the element's writing mode, directionality, and text orientation. It corresponds to the [`top`](https://developer.mozilla.org/en-US/docs/Web/CSS/top), [`right`](https://developer.mozilla.org/en-US/docs/Web/CSS/right), [`bottom`](https://developer.mozilla.org/en-US/docs/Web/CSS/bottom), or [`left`](https://developer.mozilla.org/en-US/docs/Web/CSS/left) property depending on the values defined for [`writing-mode`](https://developer.mozilla.org/en-US/docs/Web/CSS/writing-mode), [`direction`](https://developer.mozilla.org/en-US/docs/Web/CSS/direction), and [`text-orientation`](https://developer.mozilla.org/en-US/docs/Web/CSS/text-orientation).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/inset-inline-end)
   */
  insetInlineEnd?: string;
  /**
   * The `inset-inline-start` CSS property defines the logical inline start inset of an element, which maps to a physical offset depending on the element's writing mode, directionality, and text orientation. It corresponds to the [`top`](https://developer.mozilla.org/en-US/docs/Web/CSS/top), [`right`](https://developer.mozilla.org/en-US/docs/Web/CSS/right), [`bottom`](https://developer.mozilla.org/en-US/docs/Web/CSS/bottom), or [`left`](https://developer.mozilla.org/en-US/docs/Web/CSS/left) property depending on the values defined for [`writing-mode`](https://developer.mozilla.org/en-US/docs/Web/CSS/writing-mode), [`direction`](https://developer.mozilla.org/en-US/docs/Web/CSS/direction), and [`text-orientation`](https://developer.mozilla.org/en-US/docs/Web/CSS/text-orientation).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/inset-inline-start)
   */
  insetInlineStart?: string;
  /**
   * The `isolation` CSS property determines whether an element must create a new [stacking context](https://developer.mozilla.org/en-US/docs/Glossary/Stacking_context).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/isolation)
   */
  isolation?: "auto" | "isolate";
  /**
   * The CSS `justify-content` property defines how the browser distributes space between and around content items along the [main axis](https://developer.mozilla.org/en-US/docs/Glossary/Main_Axis) of a flex container and the [inline axis](https://developer.mozilla.org/en-US/docs/Glossary/Logical_properties#inline_direction) of grid and multicol containers.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/justify-content)
   */
  justifyContent?: string;
  /**
   * The CSS `justify-items` property defines the default [`justify-self`](https://developer.mozilla.org/en-US/docs/Web/CSS/justify-self) for all items of the box, giving them all a default way of justifying each box along the appropriate axis.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/justify-items)
   */
  justifyItems?: string;
  /**
   * The CSS `justify-self` property sets the way a box is justified inside its alignment container along the appropriate axis.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/justify-self)
   */
  justifySelf?: string;
  /**
   * **Experimental:** This is an [experimental technology](https://developer.mozilla.org/en-US/docs/MDN/Writing_guidelines/Experimental_deprecated_obsolete#experimental) Check the [Browser compatibility table](#browser_compatibility) carefully before using this in production.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/justify-tracks)
   */
  justifyTracks?: string;
  /**
   * The `left` CSS property participates in specifying the horizontal position of a [positioned element](https://developer.mozilla.org/en-US/docs/Web/CSS/position). This [inset property](https://developer.mozilla.org/en-US/docs/Glossary/Inset_properties) has no effect on non-positioned elements.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/left)
   */
  left?: string;
  /**
   * The `letter-spacing` CSS property sets the horizontal spacing behavior between text characters. This value is added to the natural spacing between characters while rendering the text. Positive values of `letter-spacing` causes characters to spread farther apart, while negative values of `letter-spacing` bring characters closer together.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/letter-spacing)
   */
  letterSpacing?: string;
  /**
   * The `lighting-color` attribute defines the color of the light source for lighting filter primitives.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/lighting-color)
   */
  lightingColor?: string;
  /**
   * The `line-break` CSS property sets how to break lines of Chinese, Japanese, or Korean (CJK) text when working with punctuation and symbols.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/line-break)
   */
  lineBreak?: "auto" | "loose" | "normal" | "strict" | "anywhere";
  /**
   * The `line-height` CSS property sets the height of a line box in horizontal [writing modes](https://developer.mozilla.org/en-US/docs/Web/CSS/writing-mode#vertical-rl). In vertical writing modes, it sets the width of a line box. It's commonly used to set the distance between lines of text. On block-level elements in horizontal writing modes, it specifies the preferred height of line boxes within the element, and on non-[replaced](https://developer.mozilla.org/en-US/docs/Glossary/Replaced_elements) inline elements, it specifies the height that is used to calculate line box height.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/line-height)
   */
  lineHeight?: string;
  /**
   * **Experimental:** This is an [experimental technology](https://developer.mozilla.org/en-US/docs/MDN/Writing_guidelines/Experimental_deprecated_obsolete#experimental) Check the [Browser compatibility table](#browser_compatibility) carefully before using this in production.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/line-height-step)
   */
  lineHeightStep?: string;
  /**
   * The `list-style-image` CSS property sets an image to be used as the list item marker.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/list-style-image)
   */
  listStyleImage?: string;
  /**
   * The `list-style-position` CSS property sets the position of the [`::marker`](https://developer.mozilla.org/en-US/docs/Web/CSS/::marker) relative to a list item.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/list-style-position)
   */
  listStylePosition?: "inside" | "outside";
  /**
   * The `list-style-type` CSS property sets the marker (such as a disc, character, or custom counter style) of a list item element.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/list-style-type)
   */
  listStyleType?: string;
  /**
   * The `margin-block-end` CSS property defines the logical block end margin of an element, which maps to a physical margin depending on the element's writing mode, directionality, and text orientation.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/margin-block-end)
   */
  marginBlockEnd?: string;
  /**
   * The `margin-block-start` CSS property defines the logical block start margin of an element, which maps to a physical margin depending on the element's writing mode, directionality, and text orientation.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/margin-block-start)
   */
  marginBlockStart?: string;
  /**
   * The `margin-bottom` CSS property sets the [margin area](https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_box_model/Introduction_to_the_CSS_box_model#margin_area) on the bottom of an element. A positive value places it farther from its neighbors, while a negative value places it closer.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/margin-bottom)
   */
  marginBottom?: string;
  /**
   * The `margin-inline-end` CSS property defines the logical inline end margin of an element, which maps to a physical margin depending on the element's writing mode, directionality, and text orientation. In other words, it corresponds to the [`margin-top`](https://developer.mozilla.org/en-US/docs/Web/CSS/margin-top), [`margin-right`](https://developer.mozilla.org/en-US/docs/Web/CSS/margin-right), [`margin-bottom`](https://developer.mozilla.org/en-US/docs/Web/CSS/margin-bottom) or [`margin-left`](https://developer.mozilla.org/en-US/docs/Web/CSS/margin-left) property depending on the values defined for [`writing-mode`](https://developer.mozilla.org/en-US/docs/Web/CSS/writing-mode), [`direction`](https://developer.mozilla.org/en-US/docs/Web/CSS/direction), and [`text-orientation`](https://developer.mozilla.org/en-US/docs/Web/CSS/text-orientation).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/margin-inline-end)
   */
  marginInlineEnd?: string;
  /**
   * The `margin-inline-start` CSS property defines the logical inline start margin of an element, which maps to a physical margin depending on the element's writing mode, directionality, and text orientation. It corresponds to the [`margin-top`](https://developer.mozilla.org/en-US/docs/Web/CSS/margin-top), [`margin-right`](https://developer.mozilla.org/en-US/docs/Web/CSS/margin-right), [`margin-bottom`](https://developer.mozilla.org/en-US/docs/Web/CSS/margin-bottom), or [`margin-left`](https://developer.mozilla.org/en-US/docs/Web/CSS/margin-left) property depending on the values defined for [`writing-mode`](https://developer.mozilla.org/en-US/docs/Web/CSS/writing-mode), [`direction`](https://developer.mozilla.org/en-US/docs/Web/CSS/direction), and [`text-orientation`](https://developer.mozilla.org/en-US/docs/Web/CSS/text-orientation).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/margin-inline-start)
   */
  marginInlineStart?: string;
  /**
   * The `margin-left` CSS property sets the [margin area](https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_box_model/Introduction_to_the_CSS_box_model#margin_area) on the left side of an element. A positive value places it farther from its neighbors, while a negative value places it closer.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/margin-left)
   */
  marginLeft?: string;
  /**
   * The `margin-right` CSS property sets the [margin area](https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_box_model/Introduction_to_the_CSS_box_model#margin_area) on the right side of an element. A positive value places it farther from its neighbors, while a negative value places it closer.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/margin-right)
   */
  marginRight?: string;
  /**
   * The `margin-top` CSS property sets the [margin area](https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_box_model/Introduction_to_the_CSS_box_model#margin_area) on the top of an element. A positive value places it farther from its neighbors, while a negative value places it closer.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/margin-top)
   */
  marginTop?: string;
  /**
   * **Experimental:** This is an [experimental technology](https://developer.mozilla.org/en-US/docs/MDN/Writing_guidelines/Experimental_deprecated_obsolete#experimental) Check the [Browser compatibility table](#browser_compatibility) carefully before using this in production.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/margin-trim)
   */
  marginTrim?: string;
  /**
   * The `marker-end` attribute defines the arrowhead or polymarker that will be drawn at the final vertex of the given [shape](https://developer.mozilla.org/en-US/docs/Web/SVG/Reference/Element#shape_elements).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/marker-end)
   */
  markerEnd?: string;
  /**
   * The `marker-mid` attribute defines the arrowhead or polymarker that will be drawn at all interior vertices of the given [shape](https://developer.mozilla.org/en-US/docs/Web/SVG/Reference/Element#shape_elements).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/marker-mid)
   */
  markerMid?: string;
  /**
   * The `marker-start` attribute defines the arrowhead or polymarker that will be drawn at the first vertex of the given [shape](https://developer.mozilla.org/en-US/docs/Web/SVG/Reference/Element#shape_elements).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/marker-start)
   */
  markerStart?: string;
  /**
   * The `mask-border-mode` CSS property specifies the blending mode used in a [mask border](https://developer.mozilla.org/en-US/docs/Web/CSS/mask-border).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/mask-border-mode)
   */
  maskBorderMode?: "luminance" | "alpha";
  /**
   * The `mask-border-outset` CSS property specifies the distance by which an element's [mask border](https://developer.mozilla.org/en-US/docs/Web/CSS/mask-border) is set out from its border box.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/mask-border-outset)
   */
  maskBorderOutset?: string;
  /**
   * The `mask-border-repeat` CSS property specifies how the images for the sides and the middle part of the [mask border image](https://developer.mozilla.org/en-US/docs/Web/CSS/mask-border) are scaled and tiled.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/mask-border-repeat)
   */
  maskBorderRepeat?: string;
  /**
   * The `mask-border-slice` CSS property divides the image set by [`mask-border-source`](https://developer.mozilla.org/en-US/docs/Web/CSS/mask-border-source) into regions. These regions are used to form the components of an element's [mask border](https://developer.mozilla.org/en-US/docs/Web/CSS/mask-border).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/mask-border-slice)
   */
  maskBorderSlice?: string;
  /**
   * The `mask-border-source` CSS property sets the source image used to create an element's [mask border](https://developer.mozilla.org/en-US/docs/Web/CSS/mask-border).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/mask-border-source)
   */
  maskBorderSource?: string;
  /**
   * The `mask-border-width` CSS property sets the width of an element's [mask border](https://developer.mozilla.org/en-US/docs/Web/CSS/mask-border).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/mask-border-width)
   */
  maskBorderWidth?: string;
  /**
   * The `mask-clip` CSS property determines the area which is affected by a mask. The painted content of an element must be restricted to this area.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/mask-clip)
   */
  maskClip?: string;
  /**
   * The `mask-composite` CSS property represents a compositing operation used on the current mask layer with the mask layers below it.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/mask-composite)
   */
  maskComposite?: string;
  /**
   * The `mask-image` CSS property sets the image that is used as the mask layer for an element, hiding sections of the element on which the masking image is set based on the alpha channel of the mask image and, depending on the [`mask-mode`](https://developer.mozilla.org/en-US/docs/Web/CSS/mask-mode) property value, the luminance of the mask image's colors.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/mask-image)
   */
  maskImage?: string;
  /**
   * The `mask-mode` CSS property is set on the element being masked. It sets whether the mask reference defined by the [`mask-image`](https://developer.mozilla.org/en-US/docs/Web/CSS/mask-image) is treated as a luminance or alpha mask.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/mask-mode)
   */
  maskMode?: string;
  /**
   * The `mask-origin` CSS property sets the origin of a mask. This property determines the mask positioning area: the area within which a mask image is positioned. HTML elements can have masks contained within their content border box, padding box, or content box, whereas SVG elements (which don't have the associated CSS layout boxes) can have masks contained inside their fill, stroke, or view box. For elements rendered as multiple boxes, such as a [`<span>`](https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Elements/span) of text that spans more than one line, the `mask-origin` property specifies which boxes the [`box-decoration-break`](https://developer.mozilla.org/en-US/docs/Web/CSS/box-decoration-break) property operates on to determine the mask positioning area.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/mask-origin)
   */
  maskOrigin?: string;
  /**
   * The `mask-position` CSS property sets the initial position, relative to the mask position layer set by [`mask-origin`](https://developer.mozilla.org/en-US/docs/Web/CSS/mask-origin), for each defined mask image.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/mask-position)
   */
  maskPosition?: string;
  /**
   * The `mask-repeat` CSS property sets how mask images are repeated. A mask image can be repeated along the horizontal axis, the vertical axis, both axes, or not repeated at all.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/mask-repeat)
   */
  maskRepeat?: string;
  /**
   * The `mask-size` CSS property specifies the sizes of specified mask images. Mask image sizes can be fully or partially constrained to preserve their [intrinsic aspect ratios](https://developer.mozilla.org/en-US/docs/Glossary/Aspect_ratio).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/mask-size)
   */
  maskSize?: string;
  /**
   * The `mask-type` CSS property applies to the SVG [`<mask>`](https://developer.mozilla.org/en-US/docs/Web/SVG/Reference/Element/mask) element. It defines whether to use the *luminance* (brightness) or *alpha* (transparency) content of the mask. This property may be overridden by the [`mask-mode`](https://developer.mozilla.org/en-US/docs/Web/CSS/mask-mode) property. The `mask-type` property has no effect on image or gradient masks.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/mask-type)
   */
  maskType?: string;
  /**
   * The `math-style` property indicates whether MathML equations should render with normal or compact height.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/math-style)
   */
  mathStyle?: "normal" | "compact";
  /**
   * The `max-block-size` CSS property specifies the maximum size of an element in the direction opposite that of the writing direction as specified by [`writing-mode`](https://developer.mozilla.org/en-US/docs/Web/CSS/writing-mode). That is, if the writing direction is horizontal, then `max-block-size` is equivalent to [`max-height`](https://developer.mozilla.org/en-US/docs/Web/CSS/max-height); if the writing direction is vertical, `max-block-size` is the same as [`max-width`](https://developer.mozilla.org/en-US/docs/Web/CSS/max-width).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/max-block-size)
   */
  maxBlockSize?: string;
  /**
   * The `max-height` CSS property sets the maximum height of an element. It prevents the [used value](https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_cascade/Value_processing#used_value) of the [`height`](https://developer.mozilla.org/en-US/docs/Web/CSS/height) property from becoming larger than the value specified for `max-height`.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/max-height)
   */
  maxHeight?: string;
  /**
   * The `max-inline-size` CSS property defines the horizontal or vertical maximum size of an element's block, depending on its writing mode. It corresponds to either the [`max-width`](https://developer.mozilla.org/en-US/docs/Web/CSS/max-width) or the [`max-height`](https://developer.mozilla.org/en-US/docs/Web/CSS/max-height) property, depending on the value of [`writing-mode`](https://developer.mozilla.org/en-US/docs/Web/CSS/writing-mode).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/max-inline-size)
   */
  maxInlineSize?: string;
  /**
   * The `max-width` CSS property sets the maximum width of an element. It prevents the [used value](https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_cascade/Value_processing#used_value) of the [`width`](https://developer.mozilla.org/en-US/docs/Web/CSS/width) property from becoming larger than the value specified by `max-width`.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/max-width)
   */
  maxWidth?: string;
  /**
   * The `min-block-size` CSS property defines the minimum horizontal or vertical size of an element's block, depending on its writing mode. It corresponds to either the [`min-width`](https://developer.mozilla.org/en-US/docs/Web/CSS/min-width) or the [`min-height`](https://developer.mozilla.org/en-US/docs/Web/CSS/min-height) property, depending on the value of [`writing-mode`](https://developer.mozilla.org/en-US/docs/Web/CSS/writing-mode).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/min-block-size)
   */
  minBlockSize?: string;
  /**
   * The `min-height` CSS property sets the minimum height of an element. It prevents the [used value](https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_cascade/Value_processing#used_value) of the [`height`](https://developer.mozilla.org/en-US/docs/Web/CSS/height) property from becoming smaller than the value specified for `min-height`.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/min-height)
   */
  minHeight?: string;
  /**
   * The `min-inline-size` CSS property defines the horizontal or vertical minimal size of an element's block, depending on its writing mode. It corresponds to either the [`min-width`](https://developer.mozilla.org/en-US/docs/Web/CSS/min-width) or the [`min-height`](https://developer.mozilla.org/en-US/docs/Web/CSS/min-height) property, depending on the value of [`writing-mode`](https://developer.mozilla.org/en-US/docs/Web/CSS/writing-mode).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/min-inline-size)
   */
  minInlineSize?: string;
  /**
   * The `min-width` CSS property sets the minimum width of an element. It prevents the [used value](https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_cascade/Value_processing#used_value) of the [`width`](https://developer.mozilla.org/en-US/docs/Web/CSS/width) property from becoming smaller than the value specified for `min-width`.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/min-width)
   */
  minWidth?: string;
  /**
   * The `mix-blend-mode` CSS property sets how an element's content should blend with the content of the element's parent and the element's background.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/mix-blend-mode)
   */
  mixBlendMode?:
    | "normal"
    | "multiply"
    | "screen"
    | "overlay"
    | "darken"
    | "lighten"
    | "color-dodge"
    | "color-burn"
    | "hard-light"
    | "soft-light"
    | "difference"
    | "exclusion"
    | "hue"
    | "saturation"
    | "color"
    | "luminosity";
  /**
   * The `object-fit` CSS property sets how the content of a [replaced element](https://developer.mozilla.org/en-US/docs/Glossary/Replaced_elements), such as an [`<img>`](https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Elements/img) or [`<video>`](https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Elements/video), should be resized to fit its container.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/object-fit)
   */
  objectFit?: "fill" | "contain" | "cover" | "none" | "scale-down";
  /**
   * The `object-position` CSS property specifies the alignment of the selected [replaced element](https://developer.mozilla.org/en-US/docs/Glossary/Replaced_elements)'s contents within the element's box. Areas of the box which aren't covered by the replaced element's object will show the element's background.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/object-position)
   */
  objectPosition?: string;
  /**
   * The `offset-anchor` CSS property specifies the point inside the box of an element traveling along an [`offset-path`](https://developer.mozilla.org/en-US/docs/Web/CSS/offset-path) that is actually moving along the path.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/offset-anchor)
   */
  offsetAnchor?: string;
  /**
   * The `offset-distance` CSS property specifies a position along an [`offset-path`](https://developer.mozilla.org/en-US/docs/Web/CSS/offset-path) for an element to be placed.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/offset-distance)
   */
  offsetDistance?: string;
  /**
   * The `offset-path` CSS property specifies a path for an element to follow and determines the element's positioning within the path's parent container or the SVG coordinate system. The path is a line, a curve, or a geometrical shape along which the element gets positioned or moves.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/offset-path)
   */
  offsetPath?: string;
  /**
   * The `offset-position` CSS property defines the initial position of an element along a path. This property is typically used in combination with the [`offset-path`](https://developer.mozilla.org/en-US/docs/Web/CSS/offset-path) property to create a motion effect. The value of `offset-position` determines where the element gets placed initially for moving along an offset path if an `offset-path` function such as [`path()`](https://developer.mozilla.org/en-US/docs/Web/CSS/basic-shape/path) does not specify its own starting position.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/offset-position)
   */
  offsetPosition?: string;
  /**
   * The `offset-rotate` CSS property defines the orientation/direction of the element as it is positioned along the [`offset-path`](https://developer.mozilla.org/en-US/docs/Web/CSS/offset-path).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/offset-rotate)
   */
  offsetRotate?: string;
  /**
   * The `opacity` CSS property sets the opacity of an element. Opacity is the degree to which content behind an element is hidden, and is the opposite of transparency.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/opacity)
   */
  opacity?: string;
  /**
   * The `order` CSS property sets the order to lay out an item in a flex or grid container. Items in a container are sorted by ascending `order` value and then by their source code order. Items not given an explicit `order` value are assigned the default value of `0`.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/order)
   */
  order?: string;
  /**
   * The `orphans` CSS property sets the minimum number of lines in a block container that must be shown at the *bottom* of a [page](https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_paged_media), region, or [column](https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_multicol_layout).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/orphans)
   */
  orphans?: string;
  /**
   * The `outline-color` CSS property sets the color of an element's outline.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/outline-color)
   */
  outlineColor?: string;
  /**
   * The `outline-offset` CSS property sets the amount of space between an [outline](https://developer.mozilla.org/en-US/docs/Web/CSS/outline) and the edge or border of an element.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/outline-offset)
   */
  outlineOffset?: string;
  /**
   * The `outline-style` CSS property sets the style of an element's outline. An outline is a line that is drawn around an element, outside the [`border`](https://developer.mozilla.org/en-US/docs/Web/CSS/border).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/outline-style)
   */
  outlineStyle?:
    | "none"
    | "hidden"
    | "dotted"
    | "dashed"
    | "solid"
    | "double"
    | "groove"
    | "ridge"
    | "inset"
    | "outset";
  /**
   * The CSS `outline-width` property sets the thickness of an element's outline. An outline is a line that is drawn around an element, outside the [`border`](https://developer.mozilla.org/en-US/docs/Web/CSS/border).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/outline-width)
   */
  outlineWidth?: string;
  /**
   * The `overflow-anchor` CSS property provides a way to opt out of the browser's scroll anchoring behavior, which adjusts scroll position to minimize content shifts.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/overflow-anchor)
   */
  overflowAnchor?: "auto" | "none";
  /**
   * The `overflow-block` CSS property sets what shows when content overflows the block start and block end edges of a box. This may be nothing, a scroll bar, or the overflow content.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/overflow-block)
   */
  overflowBlock?: "visible" | "hidden" | "clip" | "scroll" | "auto";
  /**
   * The `overflow-clip-margin` CSS property determines how far outside its bounds an element with [`overflow:clip`](https://developer.mozilla.org/en-US/docs/Web/CSS/overflow) may be painted before being clipped. The bound defined by this property is called the *overflow clip edge* of the box.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/overflow-clip-margin)
   */
  overflowClipMargin?: string;
  /**
   * The `overflow-inline` CSS property sets what shows when content overflows the inline start and end edges of a box. This may be nothing, a scroll bar, or the overflow content.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/overflow-inline)
   */
  overflowInline?: "visible" | "hidden" | "clip" | "scroll" | "auto";
  /**
   * The `overflow-wrap` CSS property applies to text, setting whether the browser should insert line breaks within an otherwise unbreakable string to prevent text from overflowing its line box.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/overflow-wrap)
   */
  overflowWrap?: "normal" | "break-word" | "anywhere";
  /**
   * The `overflow-x` CSS property sets what shows when content overflows a block-level element's left and right edges. This may be nothing, a scroll bar, or the overflow content. This property may also be set by using the [`overflow`](https://developer.mozilla.org/en-US/docs/Web/CSS/overflow) shorthand property.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/overflow-x)
   */
  overflowX?: "visible" | "hidden" | "clip" | "scroll" | "auto";
  /**
   * The `overflow-y` CSS property sets what shows when content overflows a block-level element's top and bottom edges. This may be nothing, a scroll bar, or the overflow content. This property may also be set by using the [`overflow`](https://developer.mozilla.org/en-US/docs/Web/CSS/overflow) shorthand property.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/overflow-y)
   */
  overflowY?: "visible" | "hidden" | "clip" | "scroll" | "auto";
  /**
   * The `overscroll-behavior-block` CSS property sets the browser's behavior when the block direction boundary of a scrolling area is reached.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/overscroll-behavior-block)
   */
  overscrollBehaviorBlock?: "auto" | "contain" | "none";
  /**
   * The `overscroll-behavior-inline` CSS property sets the browser's behavior when the inline direction boundary of a scrolling area is reached.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/overscroll-behavior-inline)
   */
  overscrollBehaviorInline?: "auto" | "contain" | "none";
  /**
   * The `overscroll-behavior-x` CSS property sets the browser's behavior when the horizontal boundary of a scrolling area is reached.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/overscroll-behavior-x)
   */
  overscrollBehaviorX?: "auto" | "contain" | "none";
  /**
   * The `overscroll-behavior-y` CSS property sets the browser's behavior when the vertical boundary of a scrolling area is reached.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/overscroll-behavior-y)
   */
  overscrollBehaviorY?: "auto" | "contain" | "none";
  /**
   * The `padding-block-end` CSS property defines the logical block end padding of an element, which maps to a physical padding depending on the element's writing mode, directionality, and text orientation.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/padding-block-end)
   */
  paddingBlockEnd?: string;
  /**
   * The `padding-block-start` CSS property defines the logical block start padding of an element, which maps to a physical padding depending on the element's writing mode, directionality, and text orientation.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/padding-block-start)
   */
  paddingBlockStart?: string;
  /**
   * The `padding-bottom` CSS property sets the height of the [padding area](https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_box_model/Introduction_to_the_CSS_box_model#padding_area) on the bottom of an element.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/padding-bottom)
   */
  paddingBottom?: string;
  /**
   * The `padding-inline-end` CSS property defines the logical inline end padding of an element, which maps to a physical padding depending on the element's writing mode, directionality, and text orientation.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/padding-inline-end)
   */
  paddingInlineEnd?: string;
  /**
   * The `padding-inline-start` CSS property defines the logical inline start padding of an element, which maps to a physical padding depending on the element's writing mode, directionality, and text orientation.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/padding-inline-start)
   */
  paddingInlineStart?: string;
  /**
   * The `padding-left` CSS property sets the width of the [padding area](https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_box_model/Introduction_to_the_CSS_box_model#padding_area) to the left of an element.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/padding-left)
   */
  paddingLeft?: string;
  /**
   * The `padding-right` CSS property sets the width of the [padding area](https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_box_model/Introduction_to_the_CSS_box_model#padding_area) on the right of an element.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/padding-right)
   */
  paddingRight?: string;
  /**
   * The `padding-top` CSS property sets the height of the [padding area](https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_box_model/Introduction_to_the_CSS_box_model#padding_area) on the top of an element.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/padding-top)
   */
  paddingTop?: string;
  /**
   * @deprecated This feature is no longer recommended. Though some browsers might still support it, it may have already been removed from the relevant web standards, may be in the process of being dropped, or may only be kept for compatibility purposes. Avoid using it, and update existing code if possible; see the [compatibility table](#browser_compatibility) at the bottom of this page to guide your decision. Be aware that this feature may cease to work at any time.
   *
   * **Warning:** This property has been replaced by the [`break-after`](https://developer.mozilla.org/en-US/docs/Web/CSS/break-after) property.
   *
   * The `page-break-after` CSS property adjusts page breaks *after* the current element.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/page-break-after)
   */
  pageBreakAfter?:
    | "auto"
    | "always"
    | "avoid"
    | "left"
    | "right"
    | "recto"
    | "verso";
  /**
   * @deprecated This feature is no longer recommended. Though some browsers might still support it, it may have already been removed from the relevant web standards, may be in the process of being dropped, or may only be kept for compatibility purposes. Avoid using it, and update existing code if possible; see the [compatibility table](#browser_compatibility) at the bottom of this page to guide your decision. Be aware that this feature may cease to work at any time.
   *
   * **Warning:** This property has been replaced by the [`break-before`](https://developer.mozilla.org/en-US/docs/Web/CSS/break-before) property.
   *
   * The `page-break-before` CSS property adjusts page breaks *before* the current element.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/page-break-before)
   */
  pageBreakBefore?:
    | "auto"
    | "always"
    | "avoid"
    | "left"
    | "right"
    | "recto"
    | "verso";
  /**
   * @deprecated This feature is no longer recommended. Though some browsers might still support it, it may have already been removed from the relevant web standards, may be in the process of being dropped, or may only be kept for compatibility purposes. Avoid using it, and update existing code if possible; see the [compatibility table](#browser_compatibility) at the bottom of this page to guide your decision. Be aware that this feature may cease to work at any time.
   *
   * **Warning:** This property has been replaced by the [`break-inside`](https://developer.mozilla.org/en-US/docs/Web/CSS/break-inside) property.
   *
   * The `page-break-inside` CSS property adjusts page breaks *inside* the current element.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/page-break-inside)
   */
  pageBreakInside?: "auto" | "avoid";
  /**
   * The `paint-order` attribute specifies the order that the fill, stroke, and markers of a given shape or text element are painted.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/paint-order)
   */
  paintOrder?:
    | "normal"
    | "fill"
    | "stroke"
    | "markers"
    | "fill stroke"
    | "fill markers"
    | "stroke fill"
    | "stroke markers"
    | "markers fill"
    | "markers stroke"
    | "fill stroke markers"
    | "fill markers stroke"
    | "stroke fill markers"
    | "stroke markers fill"
    | "markers fill stroke"
    | "markers stroke fill";
  /**
   * The `perspective` CSS property determines the distance between the z=0 plane and the user in order to give a 3D-positioned element some perspective.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/perspective)
   */
  perspective?: string;
  /**
   * The `perspective-origin` CSS property determines the position at which the viewer is looking. It is used as the *vanishing point* by the [`perspective`](https://developer.mozilla.org/en-US/docs/Web/CSS/perspective) property.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/perspective-origin)
   */
  perspectiveOrigin?: string;
  /**
   * The `pointer-events` CSS property sets under what circumstances (if any) a particular graphic element can become the [target](https://developer.mozilla.org/en-US/docs/Web/API/Event/target) of pointer events.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/pointer-events)
   */
  pointerEvents?:
    | "auto"
    | "none"
    | "visiblePainted"
    | "visibleFill"
    | "visibleStroke"
    | "visible"
    | "painted"
    | "fill"
    | "stroke"
    | "all";
  /**
   * The `position` CSS property sets how an element is positioned in a document. The [`top`](https://developer.mozilla.org/en-US/docs/Web/CSS/top), [`right`](https://developer.mozilla.org/en-US/docs/Web/CSS/right), [`bottom`](https://developer.mozilla.org/en-US/docs/Web/CSS/bottom), and [`left`](https://developer.mozilla.org/en-US/docs/Web/CSS/left) physical properties and the [`inset-block-start`](https://developer.mozilla.org/en-US/docs/Web/CSS/inset-block-start), [`inset-block-end`](https://developer.mozilla.org/en-US/docs/Web/CSS/inset-block-end), [`inset-inline-start`](https://developer.mozilla.org/en-US/docs/Web/CSS/inset-inline-start), and [`inset-inline-end`](https://developer.mozilla.org/en-US/docs/Web/CSS/inset-inline-end) flow-relative logical properties can be used to determine the final location of positioned elements.
   *
   * [MDN Reference](https://developer.mozilla.org/docs/Web/CSS/position)
   */
  position?: "absolute" | "fixed" | "relative" | "static" | "sticky";
  /**
   * The CSS `quotes` property sets how the browser should render quotation marks that are automatically added to the HTML [`<q>`](https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Elements/q) element or added using the `open-quotes` or `close-quotes` (or omitted using the `no-open-quote` and `no-close-quote`) values of the of the CSS [`content`](https://developer.mozilla.org/en-US/docs/Web/CSS/content) property.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/quotes)
   */
  quotes?: string;
  /**
   * The `r` attribute defines the radius of a circle.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/r)
   */
  r?: string;
  /**
   * The `resize` CSS property sets whether an element is resizable, and if so, in which directions.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/resize)
   */
  resize?: "none" | "both" | "horizontal" | "vertical" | "block" | "inline";
  /**
   * The `right` CSS property participates in specifying the horizontal position of a [positioned element](https://developer.mozilla.org/en-US/docs/Web/CSS/position). This [inset property](https://developer.mozilla.org/en-US/docs/Glossary/Inset_properties) has no effect on non-positioned elements.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/right)
   */
  right?: string;
  /**
   * The `rotate` CSS property allows you to specify rotation transforms individually and independently of the [`transform`](https://developer.mozilla.org/en-US/docs/Web/CSS/transform) property. This maps better to typical user interface usage, and saves having to remember the exact order of transform functions to specify in the `transform` property.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/rotate)
   */
  rotate?: string;
  /**
   * The `row-gap` CSS property sets the size of the gap ([gutter](https://developer.mozilla.org/en-US/docs/Glossary/Gutters)) between an element's rows.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/row-gap)
   */
  rowGap?: string;
  /**
   * The `ruby-align` CSS property defines the distribution of the different ruby elements over the base.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/ruby-align)
   */
  rubyAlign?: "start" | "center" | "space-between" | "space-around";
  /**
   * The `ruby-position` CSS property defines the position of a ruby element relative to its base element. It can be positioned over the element (`over`), under it (`under`), or between the characters on their right side (`inter-character`).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/ruby-position)
   */
  rubyPosition?: "over" | "under" | "inter-character";
  /**
   * The `rx` attribute defines a radius on the x-axis.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/rx)
   */
  rx?: string;
  /**
   * The `ry` attribute defines a radius on the y-axis.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/ry)
   */
  ry?: string;
  /**
   * The `scale` CSS property allows you to specify scale transforms individually and independently of the [`transform`](https://developer.mozilla.org/en-US/docs/Web/CSS/transform) property. This maps better to typical user interface usage, and saves having to remember the exact order of transform functions to specify in the `transform` value.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/scale)
   */
  scale?: string;
  /**
   * The `scroll-behavior` CSS property sets the behavior for a scrolling box when scrolling is triggered by the navigation or CSSOM scrolling APIs.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/scroll-behavior)
   */
  scrollBehavior?: "auto" | "smooth";
  /**
   * The `scroll-margin-block-end` property defines the margin of the scroll snap area at the end of the block dimension that is used for snapping this box to the [snapport](https://developer.mozilla.org/en-US/docs/Glossary/Scroll_snap#snapport). The scroll snap area is determined by taking the transformed border box, finding its rectangular bounding box (axis-aligned in the scroll container's coordinate space), then adding the specified outsets.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/scroll-margin-block-end)
   */
  scrollMarginBlockEnd?: string;
  /**
   * The `scroll-margin-block-start` property defines the margin of the scroll snap area at the start of the block dimension that is used for snapping this box to the [snapport](https://developer.mozilla.org/en-US/docs/Glossary/Scroll_snap#snapport). The scroll snap area is determined by taking the transformed border box, finding its rectangular bounding box (axis-aligned in the scroll container's coordinate space), then adding the specified outsets.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/scroll-margin-block-start)
   */
  scrollMarginBlockStart?: string;
  /**
   * The `scroll-margin-bottom` property defines the bottom margin of the scroll snap area that is used for snapping this box to the [snapport](https://developer.mozilla.org/en-US/docs/Glossary/Scroll_snap#snapport). The scroll snap area is determined by taking the transformed border box, finding its rectangular bounding box (axis-aligned in the scroll container's coordinate space), then adding the specified outsets.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/scroll-margin-bottom)
   */
  scrollMarginBottom?: string;
  /**
   * The `scroll-margin-inline-end` property defines the margin of the scroll snap area at the end of the inline dimension that is used for snapping this box to the [snapport](https://developer.mozilla.org/en-US/docs/Glossary/Scroll_snap#snapport). The scroll snap area is determined by taking the transformed border box, finding its rectangular bounding box (axis-aligned in the scroll container's coordinate space), then adding the specified outsets.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/scroll-margin-inline-end)
   */
  scrollMarginInlineEnd?: string;
  /**
   * The `scroll-margin-inline-start` property defines the margin of the scroll snap area at the start of the inline dimension that is used for snapping this box to the [snapport](https://developer.mozilla.org/en-US/docs/Glossary/Scroll_snap#snapport). The scroll snap area is determined by taking the transformed border box, finding its rectangular bounding box (axis-aligned in the scroll container's coordinate space), then adding the specified outsets.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/scroll-margin-inline-start)
   */
  scrollMarginInlineStart?: string;
  /**
   * The `scroll-margin-left` property defines the left margin of the scroll snap area that is used for snapping this box to the [snapport](https://developer.mozilla.org/en-US/docs/Glossary/Scroll_snap#snapport). The scroll snap area is determined by taking the transformed border box, finding its rectangular bounding box (axis-aligned in the scroll container's coordinate space), then adding the specified outsets.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/scroll-margin-left)
   */
  scrollMarginLeft?: string;
  /**
   * The `scroll-margin-right` property defines the right margin of the scroll snap area that is used for snapping this box to the [snapport](https://developer.mozilla.org/en-US/docs/Glossary/Scroll_snap#snapport). The scroll snap area is determined by taking the transformed border box, finding its rectangular bounding box (axis-aligned in the scroll container's coordinate space), then adding the specified outsets.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/scroll-margin-right)
   */
  scrollMarginRight?: string;
  /**
   * The `scroll-margin-top` property defines the top margin of the scroll snap area that is used for snapping this box to the [snapport](https://developer.mozilla.org/en-US/docs/Glossary/Scroll_snap#snapport). The scroll snap area is determined by taking the transformed border box, finding its rectangular bounding box (axis-aligned in the scroll container's coordinate space), then adding the specified outsets.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/scroll-margin-top)
   */
  scrollMarginTop?: string;
  /**
   * The `scroll-padding-block-end` property defines offsets for the end edge in the block dimension of the *optimal viewing region* of the scrollport: the region used as the target region for placing things in view of the user. This allows the author to exclude regions of the scrollport that are obscured by other content (such as fixed-positioned toolbars or sidebars) or to put more breathing room between a targeted element and the edges of the scrollport.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/scroll-padding-block-end)
   */
  scrollPaddingBlockEnd?: string;
  /**
   * The `scroll-padding-block-start` property defines offsets for the start edge in the block dimension of the *optimal viewing region* of the scrollport: the region used as the target region for placing things in view of the user. This allows the author to exclude regions of the scrollport that are obscured by other content (such as fixed-positioned toolbars or sidebars) or to put more breathing room between a targeted element and the edges of the scrollport.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/scroll-padding-block-start)
   */
  scrollPaddingBlockStart?: string;
  /**
   * The `scroll-padding-bottom` property defines offsets for the bottom of the *optimal viewing region* of the scrollport: the region used as the target region for placing things in view of the user. This allows the author to exclude regions of the scrollport that are obscured by other content (such as fixed-positioned toolbars or sidebars) or to put more breathing room between a targeted element and the edges of the scrollport.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/scroll-padding-bottom)
   */
  scrollPaddingBottom?: string;
  /**
   * The `scroll-padding-inline-end` property defines offsets for the end edge in the inline dimension of the *optimal viewing region* of the scrollport: the region used as the target region for placing things in view of the user. This allows the author to exclude regions of the scrollport that are obscured by other content (such as fixed-positioned toolbars or sidebars) or to put more breathing room between a targeted element and the edges of the scrollport.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/scroll-padding-inline-end)
   */
  scrollPaddingInlineEnd?: string;
  /**
   * The `scroll-padding-inline-start` property defines offsets for the start edge in the inline dimension of the *optimal viewing region* of the scrollport: the region used as the target region for placing things in view of the user. This allows the author to exclude regions of the scrollport that are obscured by other content (such as fixed-positioned toolbars or sidebars) or to put more breathing room between a targeted element and the edges of the scrollport.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/scroll-padding-inline-start)
   */
  scrollPaddingInlineStart?: string;
  /**
   * The `scroll-padding-left` property defines offsets for the left of the *optimal viewing region* of the scrollport: the region used as the target region for placing things in view of the user. This allows the author to exclude regions of the scrollport that are obscured by other content (such as fixed-positioned toolbars or sidebars) or to put more breathing room between a targeted element and the edges of the scrollport.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/scroll-padding-left)
   */
  scrollPaddingLeft?: string;
  /**
   * The `scroll-padding-right` property defines offsets for the right of the *optimal viewing region* of the scrollport: the region used as the target region for placing things in view of the user. This allows the author to exclude regions of the scrollport that are obscured by other content (such as fixed-positioned toolbars or sidebars) or to put more breathing room between a targeted element and the edges of the scrollport.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/scroll-padding-right)
   */
  scrollPaddingRight?: string;
  /**
   * The `scroll-padding-top` property defines offsets for the top of the *optimal viewing region* of the scrollport: the region used as the target region for placing things in view of the user. This allows the author to exclude regions of the scrollport that are obscured by other content (such as fixed-positioned toolbars or sidebars) or to put more breathing room between a targeted element and the edges of the scrollport.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/scroll-padding-top)
   */
  scrollPaddingTop?: string;
  /**
   * The `scroll-snap-align` property specifies the box's snap position as an alignment of its [snap area](https://developer.mozilla.org/en-US/docs/Glossary/Scroll_snap#snap_area) (as the [alignment subject](https://developer.mozilla.org/en-US/docs/Glossary/Alignment_Subject)) within its snap container's snap port (as the [alignment container](https://developer.mozilla.org/en-US/docs/Glossary/Alignment_Container)).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/scroll-snap-align)
   */
  scrollSnapAlign?: "none" | "start" | "end" | "center";
  /**
   * The `scroll-snap-stop` CSS property defines whether or not the scroll container is allowed to "pass over" possible snap positions.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/scroll-snap-stop)
   */
  scrollSnapStop?: "normal" | "always";
  /**
   * The `scroll-snap-type` CSS property is set on a [scroll container](https://developer.mozilla.org/en-US/docs/Glossary/Scroll_container), opting it into scroll snapping by setting the direction and strictness of snap point enforcement within the [snap port](https://developer.mozilla.org/en-US/docs/Glossary/Scroll_snap#snapport).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/scroll-snap-type)
   */
  scrollSnapType?:
    | "none"
    | "x"
    | "y"
    | "block"
    | "inline"
    | "both"
    | "x mandatory"
    | "y mandatory"
    | "block mandatory"
    | "inline mandatory"
    | "both mandatory"
    | "x proximity"
    | "y proximity"
    | "block proximity"
    | "inline proximity"
    | "both proximity";
  /**
   * The `scrollbar-color` CSS property sets the color of the scrollbar track and thumb.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/scrollbar-color)
   */
  scrollbarColor?: string;
  /**
   * The `scrollbar-gutter` CSS property allows authors to reserve space for the scrollbar, preventing unwanted layout changes as the content grows while also avoiding unnecessary visuals when scrolling isn't needed.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/scrollbar-gutter)
   */
  scrollbarGutter?: "auto" | "stable" | "stable both-edges";
  /**
   * The `scrollbar-width` property allows the author to set the desired thickness of an element's scrollbars when they are shown.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/scrollbar-width)
   */
  scrollbarWidth?: "auto" | "thin" | "none";
  /**
   * The `shape-image-threshold` CSS property sets the alpha channel threshold used to extract the shape using an image as the value for [`shape-outside`](https://developer.mozilla.org/en-US/docs/Web/CSS/shape-outside).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/shape-image-threshold)
   */
  shapeImageThreshold?: string;
  /**
   * The `shape-margin` CSS property sets a margin for a CSS shape created using [`shape-outside`](https://developer.mozilla.org/en-US/docs/Web/CSS/shape-outside).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/shape-margin)
   */
  shapeMargin?: string;
  /**
   * The `shape-outside` CSS property defines a shape—which may be non-rectangular—around which adjacent inline content should wrap. By default, inline content wraps around its margin box; `shape-outside` provides a way to customize this wrapping, making it possible to wrap text around complex objects rather than rectangular boxes.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/shape-outside)
   */
  shapeOutside?: string;
  /**
   * The `shape-rendering` attribute provides hints to the renderer about what tradeoffs to make when rendering shapes like paths, circles, or rectangles.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/shape-rendering)
   */
  shapeRendering?:
    | "auto"
    | "optimizeSpeed"
    | "crispEdges"
    | "geometricPrecision";
  /**
   * The `stop-color` attribute indicates what color to use at a gradient stop.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/stop-color)
   */
  stopColor?: string;
  /**
   * The `stop-opacity` attribute defines the opacity of a given color gradient stop.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/stop-opacity)
   */
  stopOpacity?: string;
  /**
   * The `stroke` attribute is a presentation attribute defining the color (*or any SVG paint servers like gradients or patterns*) used to paint the outline of the shape.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/stroke)
   */
  stroke?: string;
  /**
   * The `stroke-dasharray` attribute is a presentation attribute defining the pattern of dashes and gaps used to paint the outline of the shape.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/stroke-dasharray)
   */
  strokeDasharray?: string;
  /**
   * The `stroke-dashoffset` attribute is a presentation attribute defining an offset on the rendering of the associated dash array.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/stroke-dashoffset)
   */
  strokeDashoffset?: string;
  /**
   * The `stroke-linecap` attribute is a presentation attribute defining the shape to be used at the end of open subpaths when they are stroked.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/stroke-linecap)
   */
  strokeLinecap?: "butt" | "round" | "square";
  /**
   * The `stroke-linejoin` attribute is a presentation attribute defining the shape to be used at the corners of paths when they are stroked.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/stroke-linejoin)
   */
  strokeLinejoin?: "arcs" | "bevel" | "miter" | "miter-clip" | "round";
  /**
   * The `stroke-miterlimit` attribute is a presentation attribute defining a limit on the ratio of the miter length to the [`stroke-width`](https://developer.mozilla.org/en-US/docs/Web/SVG/Reference/Attribute/stroke-width) used to draw a miter join. When the limit is exceeded, the join is converted from a miter to a bevel.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/stroke-miterlimit)
   */
  strokeMiterlimit?: string;
  /**
   * The `stroke-opacity` attribute is a presentation attribute defining the opacity of the paint server (*color*, *gradient*, *pattern*, etc.) applied to the stroke of a shape.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/stroke-opacity)
   */
  strokeOpacity?: string;
  /**
   * The `stroke-width` attribute is a presentation attribute defining the width of the stroke to be applied to the shape. It applies to any SVG shape or text-content element, but as an inherited property, it may be applied to elements such as [`<g>`](https://developer.mozilla.org/en-US/docs/Web/SVG/Reference/Element/g) and still have the intended effect on descendant elements' strokes.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/stroke-width)
   */
  strokeWidth?: string;
  /**
   * The `table-layout` CSS property sets the algorithm used to lay out [`<table>`](https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Elements/table) cells, rows, and columns.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/table-layout)
   */
  tableLayout?: "auto" | "fixed";
  /**
   * The `tab-size` CSS property is used to customize the width of tab characters (U+0009).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/tab-size)
   */
  tabSize?: string;
  /**
   * The `text-align` CSS property sets the horizontal alignment of the inline-level content inside a block element or table-cell box. This means it works like [`vertical-align`](https://developer.mozilla.org/en-US/docs/Web/CSS/vertical-align) but in the horizontal direction.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/text-align)
   */
  textAlign?: "left" | "right" | "center" | "justify" | "start" | "end";
  /**
   * The `text-align-last` CSS property sets how the last line of a block or a line, right before a forced line break, is aligned.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/text-align-last)
   */
  textAlignLast?:
    | "auto"
    | "left"
    | "right"
    | "center"
    | "justify"
    | "start"
    | "end";
  /**
   * The `text-anchor` attribute is used to align (start-, middle- or end-alignment) a string of pre-formatted text or auto-wrapped text where the wrapping area is determined from the [`inline-size`](https://developer.mozilla.org/en-US/docs/Web/CSS/inline-size) property relative to a given point.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/text-anchor)
   */
  textAnchor?: "start" | "middle" | "end";
  /**
   * The `text-combine-upright` CSS property sets the combination of characters into the space of a single character. If the combined text is wider than 1em, the user agent must fit the contents within 1em. The resulting composition is treated as a single upright glyph for layout and decoration. This property only has an effect in vertical writing modes.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/text-combine-upright)
   */
  textCombineUpright?: "none" | "all" | "digits";
  /**
   * The `text-decoration-color` CSS property sets the color of decorations added to text by [`text-decoration-line`](https://developer.mozilla.org/en-US/docs/Web/CSS/text-decoration-line).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/text-decoration-color)
   */
  textDecorationColor?: string;
  /**
   * The `text-decoration-line` CSS property sets the kind of decoration that is used on text in an element, such as an underline or overline.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/text-decoration-line)
   */
  textDecorationLine?: string;
  /**
   * **Experimental:** This is an [experimental technology](https://developer.mozilla.org/en-US/docs/MDN/Writing_guidelines/Experimental_deprecated_obsolete#experimental) Check the [Browser compatibility table](#browser_compatibility) carefully before using this in production.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/text-decoration-skip)
   */
  textDecorationSkip?: string;
  /**
   * The `text-decoration-skip-ink` CSS property specifies how overlines and underlines are drawn when they pass over glyph ascenders and descenders.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/text-decoration-skip-ink)
   */
  textDecorationSkipInk?: "none" | "auto" | "all";
  /**
   * The `text-decoration-style` CSS property sets the style of the lines specified by [`text-decoration-line`](https://developer.mozilla.org/en-US/docs/Web/CSS/text-decoration-line). The style applies to all lines that are set with `text-decoration-line`.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/text-decoration-style)
   */
  textDecorationStyle?: "solid" | "double" | "dotted" | "dashed" | "wavy";
  /**
   * The `text-decoration-thickness` CSS property sets the stroke thickness of the decoration line that is used on text in an element, such as a line-through, underline, or overline.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/text-decoration-thickness)
   */
  textDecorationThickness?: string;
  /**
   * The `text-emphasis-color` CSS property sets the color of emphasis marks. This value can also be set using the [`text-emphasis`](https://developer.mozilla.org/en-US/docs/Web/CSS/text-emphasis) shorthand.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/text-emphasis-color)
   */
  textEmphasisColor?: string;
  /**
   * The `text-emphasis-position` CSS property sets where emphasis marks are drawn. Similar to the text rendered by the [`<ruby>`](https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Elements/ruby) HTML element, if there isn't enough room for emphasis marks, the line height is increased.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/text-emphasis-position)
   */
  textEmphasisPosition?:
    | "auto"
    | "over"
    | "under"
    | "over right"
    | "over left"
    | "under right"
    | "under left"
    | "left over"
    | "right over"
    | "right under"
    | "left under";
  /**
   * The `text-emphasis-style` CSS property sets the appearance of emphasis marks. It can also be set, and reset, using the [`text-emphasis`](https://developer.mozilla.org/en-US/docs/Web/CSS/text-emphasis) shorthand.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/text-emphasis-style)
   */
  textEmphasisStyle?: string;
  /**
   * The `text-indent` CSS property sets the length of empty space (indentation) that is put before lines of text in a block.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/text-indent)
   */
  textIndent?: string;
  /**
   * The `text-justify` CSS property sets what type of justification should be applied to text when [`text-align: justify;`](https://developer.mozilla.org/en-US/docs/Web/CSS/text-align) is set on an element.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/text-justify)
   */
  textJustify?: "auto" | "inter-word" | "inter-character" | "none";
  /**
   * The `text-orientation` CSS property sets the orientation of the text characters in a line. It only affects text in vertical mode (when [`writing-mode`](https://developer.mozilla.org/en-US/docs/Web/CSS/writing-mode) is not `horizontal-tb`). It is useful for controlling the display of languages that use vertical script, and also for making vertical table headers.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/text-orientation)
   */
  textOrientation?: "mixed" | "upright" | "sideways";
  /**
   * The `text-overflow` CSS property sets how hidden overflow content is signaled to users. It can be clipped, display an ellipsis (`…`), or display a custom string.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/text-overflow)
   */
  textOverflow?: "clip" | "ellipsis";
  /**
   * The `text-rendering` CSS property provides information to the rendering engine about what to optimize for when rendering text.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/text-rendering)
   */
  textRendering?:
    | "auto"
    | "optimizeSpeed"
    | "optimizeLegibility"
    | "geometricPrecision";
  /**
   * The `text-shadow` CSS property adds shadows to text. It accepts a comma-separated list of shadows to be applied to the text and any of its [`decorations`](https://developer.mozilla.org/en-US/docs/Web/CSS/text-decoration). Each shadow is described by some combination of X and Y offsets from the element, blur radius, and color.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/text-shadow)
   */
  textShadow?: string;
  /**
   * **Experimental:** This is an [experimental technology](https://developer.mozilla.org/en-US/docs/MDN/Writing_guidelines/Experimental_deprecated_obsolete#experimental) Check the [Browser compatibility table](#browser_compatibility) carefully before using this in production.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/text-size-adjust)
   */
  textSizeAdjust?: string;
  /**
   * The `text-transform` CSS property specifies how to capitalize an element's text. It can be used to make text appear in all-uppercase or all-lowercase, or with each word capitalized. It also can help improve legibility for ruby.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/text-transform)
   */
  textTransform?:
    | "none"
    | "capitalize"
    | "uppercase"
    | "lowercase"
    | "full-width"
    | "full-size-kana";
  /**
   * The `text-underline-offset` CSS property sets the offset distance of an underline text decoration line (applied using [`text-decoration`](https://developer.mozilla.org/en-US/docs/Web/CSS/text-decoration)) from its original position.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/text-underline-offset)
   */
  textUnderlineOffset?: string;
  /**
   * The `text-underline-position` CSS property specifies the position of the underline which is set using the [`text-decoration`](https://developer.mozilla.org/en-US/docs/Web/CSS/text-decoration) property's `underline` value.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/text-underline-position)
   */
  textUnderlinePosition?: "auto" | "under" | "left" | "right";
  /**
   * The `text-wrap-mode` CSS property controls whether the text inside an element is wrapped. The different values provide alternate ways of wrapping the content of a block element. It can also be set, and reset, using the [`text-wrap`](https://developer.mozilla.org/en-US/docs/Web/CSS/text-wrap) shorthand or the [`white-space`](https://developer.mozilla.org/en-US/docs/Web/CSS/white-space) shorthand.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/text-wrap-mode)
   */
  textWrapMode?: "wrap" | "nowrap";
  /**
   * The `text-wrap-style` CSS property controls how text inside an element is wrapped. The different values provide alternate ways of wrapping the content of a block element. It can also be set, and reset, using the [`text-wrap`](https://developer.mozilla.org/en-US/docs/Web/CSS/text-wrap) shorthand.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/text-wrap-style)
   */
  textWrapStyle?: "auto" | "balance" | "pretty" | "stable";
  /**
   * The `top` CSS property sets the vertical position of a [positioned element](https://developer.mozilla.org/en-US/docs/Web/CSS/position). This [inset property](https://developer.mozilla.org/en-US/docs/Glossary/Inset_properties) has no effect on non-positioned elements.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/top)
   */
  top?: string;
  /**
   * The `touch-action` CSS property sets how an element's region can be manipulated by a touchscreen user (for example, by zooming features built into the browser).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/touch-action)
   */
  touchAction?:
    | "auto"
    | "none"
    | "manipulation"
    | "pan-x"
    | "pan-y"
    | "pan-left"
    | "pan-right"
    | "pan-up"
    | "pan-down"
    | "pinch-zoom"
    | "pan-x pan-y"
    | "pan-x pan-up"
    | "pan-x pan-down"
    | "pan-y pan-left"
    | "pan-y pan-right"
    | "pan-left pan-up"
    | "pan-left pan-down"
    | "pan-right pan-up"
    | "pan-right pan-down"
    | "pan-x pinch-zoom"
    | "pan-y pinch-zoom"
    | "pan-left pinch-zoom"
    | "pan-right pinch-zoom"
    | "pan-up pinch-zoom"
    | "pan-down pinch-zoom"
    | "pan-x pan-y pinch-zoom"
    | "pan-x pan-up pinch-zoom"
    | "pan-x pan-down pinch-zoom"
    | "pan-y pan-left pinch-zoom"
    | "pan-y pan-right pinch-zoom"
    | "pan-left pan-up pinch-zoom"
    | "pan-left pan-down pinch-zoom"
    | "pan-right pan-up pinch-zoom"
    | "pan-right pan-down pinch-zoom";
  /**
   * The `transform` CSS property lets you rotate, scale, skew, or translate an element. It modifies the coordinate space of the CSS [visual formatting model](https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_display/Visual_formatting_model).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/transform)
   */
  transform?: string;
  /**
   * The `transform-box` CSS property defines the layout box to which the [`transform`](https://developer.mozilla.org/en-US/docs/Web/CSS/transform), individual transform properties [`translate`](https://developer.mozilla.org/en-US/docs/Web/CSS/translate), [`scale`](https://developer.mozilla.org/en-US/docs/Web/CSS/scale), and [`rotate`](https://developer.mozilla.org/en-US/docs/Web/CSS/rotate), and [`transform-origin`](https://developer.mozilla.org/en-US/docs/Web/CSS/transform-origin) properties relate.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/transform-box)
   */
  transformBox?:
    | "content-box"
    | "border-box"
    | "fill-box"
    | "stroke-box"
    | "view-box";
  /**
   * The `transform-origin` CSS property sets the origin for an element's transformations.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/transform-origin)
   */
  transformOrigin?: string;
  /**
   * The `transform-style` CSS property sets whether children of an element are positioned in the 3D space or are flattened in the plane of the element.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/transform-style)
   */
  transformStyle?: "flat" | "preserve-3d";
  /**
   * The `transition-delay` CSS property specifies the duration to wait before starting a property's [transition effect](https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_transitions/Using_CSS_transitions) when its value changes.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/transition-delay)
   */
  transitionDelay?: string;
  /**
   * The `transition-duration` CSS property sets the length of time a transition animation should take to complete. By default, the value is `0s`, meaning that no animation will occur.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/transition-duration)
   */
  transitionDuration?: string;
  /**
   * The `transition-property` CSS property sets the CSS properties to which a [transition effect](https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_transitions/Using_CSS_transitions) should be applied.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/transition-property)
   */
  transitionProperty?: string;
  /**
   * The `transition-timing-function` CSS property sets how intermediate values are calculated for CSS properties being affected by a [transition effect](https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_transitions/Using_CSS_transitions).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/transition-timing-function)
   */
  transitionTimingFunction?: string;
  /**
   * The `translate` CSS property allows you to specify translation transforms individually and independently of the [`transform`](https://developer.mozilla.org/en-US/docs/Web/CSS/transform) property. This maps better to typical user interface usage, and saves having to remember the exact order of transform functions to specify in the `transform` value.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/translate)
   */
  translate?: string;
  /**
   * The `unicode-bidi` CSS property, together with the [`direction`](https://developer.mozilla.org/en-US/docs/Web/CSS/direction) property, determines how bidirectional text in a document is handled. For example, if a block of content contains both left-to-right and right-to-left text, the user-agent uses a complex Unicode algorithm to decide how to display the text. The `unicode-bidi` property overrides this algorithm and allows the developer to control the text embedding.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/unicode-bidi)
   */
  unicodeBidi?:
    | "normal"
    | "embed"
    | "isolate"
    | "bidi-override"
    | "isolate-override"
    | "plaintext";
  /**
   * The `user-select` CSS property controls whether the user can select text. This doesn't have any effect on content loaded as part of a browser's user interface (its [chrome](https://developer.mozilla.org/en-US/docs/Glossary/Chrome)), except in textboxes.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/user-select)
   */
  userSelect?: "auto" | "text" | "none" | "contain" | "all";
  /**
   * The `vector-effect` property specifies the vector effect to use when drawing an object. Vector effects are applied before any of the other compositing operations, i.e., filters, masks and clips.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/vector-effect)
   */
  vectorEffect?:
    | "none"
    | "non-scaling-stroke"
    | "non-scaling-size"
    | "non-rotation"
    | "fixed-position";
  /**
   * The `vertical-align` CSS property sets vertical alignment of an inline, inline-block or table-cell box.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/vertical-align)
   */
  verticalAlign?: string;
  /**
   * The `visibility` CSS property shows or hides an element without changing the layout of a document. The property can also hide rows or columns in a [`<table>`](https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Elements/table).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/visibility)
   */
  visibility?: "visible" | "hidden" | "collapse";
  /**
   * The `white-space` CSS property sets how [white space](https://developer.mozilla.org/en-US/docs/Glossary/Whitespace) inside an element is handled.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/white-space)
   */
  whiteSpace?:
    | "normal"
    | "nowrap"
    | "pre"
    | "pre-wrap"
    | "pre-line"
    | "break-spaces";
  /**
   * The `widows` CSS property sets the minimum number of lines in a block container that must be shown at the *top* of a [page](https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_paged_media), region, or [column](https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_multicol_layout).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/widows)
   */
  widows?: string;
  /**
   * The `width` CSS property sets an element's width. By default, it sets the width of the [content area](https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_box_model/Introduction_to_the_CSS_box_model#content_area), but if [`box-sizing`](https://developer.mozilla.org/en-US/docs/Web/CSS/box-sizing) is set to `border-box`, it sets the width of the [border area](https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_box_model/Introduction_to_the_CSS_box_model#border_area).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/width)
   */
  width?: string;
  /**
   * The `will-change` CSS property hints to browsers how an element is expected to change. Browsers may set up optimizations before an element is actually changed. These kinds of optimizations can increase the responsiveness of a page by doing potentially expensive work before they are actually required.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/will-change)
   */
  willChange?: string;
  /**
   * The `word-break` CSS property sets whether line breaks appear wherever the text would otherwise overflow its content box.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/word-break)
   */
  wordBreak?: "normal" | "break-all" | "keep-all" | "break-word";
  /**
   * The `word-spacing` CSS property sets the length of space between words and between tags.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/word-spacing)
   */
  wordSpacing?: string;
  /**
   * The `writing-mode` CSS property sets whether lines of text are laid out horizontally or vertically, as well as the direction in which blocks progress. When set for an entire document, it should be set on the root element (`html` element for HTML documents).
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/writing-mode)
   */
  writingMode?:
    | "horizontal-tb"
    | "vertical-rl"
    | "vertical-lr"
    | "sideways-rl"
    | "sideways-lr";
  /**
   * The `x` attribute defines an x-axis coordinate in the user coordinate system.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/x)
   */
  x?: string;
  /**
   * The `y` attribute defines a y-axis coordinate in the user coordinate system.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/y)
   */
  y?: string;
  /**
   * The `z-index` CSS property sets the z-order of a [positioned](https://developer.mozilla.org/en-US/docs/Web/CSS/position) element and its descendants or flex and grid items. Overlapping elements with a larger z-index cover those with a smaller one.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/z-index)
   */
  zIndex?: string;
};

// Update this object using `Add missing properties` code action
// after adding a new property to `Style`.
const style: {
  [K in keyof Style]-?: null;
} = {
  alignContent: null,
  alignItems: null,
  alignmentBaseline: null,
  alignSelf: null,
  alignTracks: null,
  animationDelay: null,
  animationDirection: null,
  animationDuration: null,
  animationFillMode: null,
  animationIterationCount: null,
  animationName: null,
  animationPlayState: null,
  animationTimingFunction: null,
  appearance: null,
  aspectRatio: null,
  backdropFilter: null,
  backfaceVisibility: null,
  backgroundAttachment: null,
  backgroundBlendMode: null,
  backgroundClip: null,
  backgroundColor: null,
  backgroundImage: null,
  backgroundOrigin: null,
  backgroundPositionX: null,
  backgroundPositionY: null,
  backgroundRepeat: null,
  backgroundSize: null,
  baselineShift: null,
  blockSize: null,
  borderBlockEndColor: null,
  borderBlockEndStyle: null,
  borderBlockEndWidth: null,
  borderBlockStartColor: null,
  borderBlockStartStyle: null,
  borderBlockStartWidth: null,
  borderBottomColor: null,
  borderBottomLeftRadius: null,
  borderBottomRightRadius: null,
  borderBottomStyle: null,
  borderBottomWidth: null,
  borderCollapse: null,
  borderEndEndRadius: null,
  borderEndStartRadius: null,
  borderImageOutset: null,
  borderImageRepeat: null,
  borderImageSlice: null,
  borderImageSource: null,
  borderImageWidth: null,
  borderInlineEndColor: null,
  borderInlineEndStyle: null,
  borderInlineEndWidth: null,
  borderInlineStartColor: null,
  borderInlineStartStyle: null,
  borderInlineStartWidth: null,
  borderLeftColor: null,
  borderLeftStyle: null,
  borderLeftWidth: null,
  borderRightColor: null,
  borderRightStyle: null,
  borderRightWidth: null,
  borderSpacing: null,
  borderStartEndRadius: null,
  borderStartStartRadius: null,
  borderTopColor: null,
  borderTopLeftRadius: null,
  borderTopRightRadius: null,
  borderTopStyle: null,
  borderTopWidth: null,
  bottom: null,
  boxDecorationBreak: null,
  boxShadow: null,
  boxSizing: null,
  breakAfter: null,
  breakBefore: null,
  breakInside: null,
  captionSide: null,
  caretColor: null,
  clear: null,
  clip: null,
  clipPath: null,
  clipRule: null,
  color: null,
  colorInterpolation: null,
  colorInterpolationFilters: null,
  colorScheme: null,
  columnCount: null,
  columnFill: null,
  columnGap: null,
  columnRuleColor: null,
  columnRuleStyle: null,
  columnRuleWidth: null,
  columnSpan: null,
  columnWidth: null,
  contain: null,
  content: null,
  contentVisibility: null,
  counterIncrement: null,
  counterReset: null,
  counterSet: null,
  cursor: null,
  cx: null,
  cy: null,
  d: null,
  direction: null,
  display: null,
  dominantBaseline: null,
  emptyCells: null,
  fill: null,
  fillOpacity: null,
  fillRule: null,
  fieldSizing: null,
  filter: null,
  flexBasis: null,
  flexDirection: null,
  flexGrow: null,
  flexShrink: null,
  flexWrap: null,
  float: null,
  floodColor: null,
  floodOpacity: null,
  fontFamily: null,
  fontFeatureSettings: null,
  fontKerning: null,
  fontLanguageOverride: null,
  fontOpticalSizing: null,
  fontSize: null,
  fontSizeAdjust: null,
  fontStretch: null,
  fontStyle: null,
  fontSynthesisSmallCaps: null,
  fontSynthesisStyle: null,
  fontSynthesisWeight: null,
  fontVariantAlternates: null,
  fontVariantCaps: null,
  fontVariantEastAsian: null,
  fontVariantEmoji: null,
  fontVariantLigatures: null,
  fontVariantNumeric: null,
  fontVariantPosition: null,
  fontVariationSettings: null,
  fontWeight: null,
  forcedColorAdjust: null,
  glyphOrientationHorizontal: null,
  glyphOrientationVertical: null,
  gridAutoColumns: null,
  gridAutoFlow: null,
  gridAutoRows: null,
  gridColumnEnd: null,
  gridColumnStart: null,
  gridRowEnd: null,
  gridRowStart: null,
  gridTemplateAreas: null,
  gridTemplateColumns: null,
  gridTemplateRows: null,
  hangingPunctuation: null,
  height: null,
  hyphens: null,
  imageOrientation: null,
  imageRendering: null,
  imageResolution: null,
  initialLetter: null,
  inlineSize: null,
  insetBlockEnd: null,
  insetBlockStart: null,
  insetInlineEnd: null,
  insetInlineStart: null,
  isolation: null,
  justifyContent: null,
  justifyItems: null,
  justifySelf: null,
  justifyTracks: null,
  left: null,
  letterSpacing: null,
  lightingColor: null,
  lineBreak: null,
  lineHeight: null,
  lineHeightStep: null,
  listStyleImage: null,
  listStylePosition: null,
  listStyleType: null,
  marginBlockEnd: null,
  marginBlockStart: null,
  marginBottom: null,
  marginInlineEnd: null,
  marginInlineStart: null,
  marginLeft: null,
  marginRight: null,
  marginTop: null,
  marginTrim: null,
  markerEnd: null,
  markerMid: null,
  markerStart: null,
  maskBorderMode: null,
  maskBorderOutset: null,
  maskBorderRepeat: null,
  maskBorderSlice: null,
  maskBorderSource: null,
  maskBorderWidth: null,
  maskClip: null,
  maskComposite: null,
  maskImage: null,
  maskMode: null,
  maskOrigin: null,
  maskPosition: null,
  maskRepeat: null,
  maskSize: null,
  maskType: null,
  mathStyle: null,
  maxBlockSize: null,
  maxHeight: null,
  maxInlineSize: null,
  maxWidth: null,
  minBlockSize: null,
  minHeight: null,
  minInlineSize: null,
  minWidth: null,
  mixBlendMode: null,
  objectFit: null,
  objectPosition: null,
  offsetAnchor: null,
  offsetDistance: null,
  offsetPath: null,
  offsetPosition: null,
  offsetRotate: null,
  opacity: null,
  order: null,
  orphans: null,
  outlineColor: null,
  outlineOffset: null,
  outlineStyle: null,
  outlineWidth: null,
  overflowAnchor: null,
  overflowBlock: null,
  overflowClipMargin: null,
  overflowInline: null,
  overflowWrap: null,
  overflowX: null,
  overflowY: null,
  overscrollBehaviorBlock: null,
  overscrollBehaviorInline: null,
  overscrollBehaviorX: null,
  overscrollBehaviorY: null,
  paddingBlockEnd: null,
  paddingBlockStart: null,
  paddingBottom: null,
  paddingInlineEnd: null,
  paddingInlineStart: null,
  paddingLeft: null,
  paddingRight: null,
  paddingTop: null,
  pageBreakAfter: null,
  pageBreakBefore: null,
  pageBreakInside: null,
  paintOrder: null,
  perspective: null,
  perspectiveOrigin: null,
  pointerEvents: null,
  position: null,
  quotes: null,
  r: null,
  resize: null,
  right: null,
  rotate: null,
  rowGap: null,
  rubyAlign: null,
  rubyPosition: null,
  rx: null,
  ry: null,
  scale: null,
  scrollBehavior: null,
  scrollMarginBlockEnd: null,
  scrollMarginBlockStart: null,
  scrollMarginBottom: null,
  scrollMarginInlineEnd: null,
  scrollMarginInlineStart: null,
  scrollMarginLeft: null,
  scrollMarginRight: null,
  scrollMarginTop: null,
  scrollPaddingBlockEnd: null,
  scrollPaddingBlockStart: null,
  scrollPaddingBottom: null,
  scrollPaddingInlineEnd: null,
  scrollPaddingInlineStart: null,
  scrollPaddingLeft: null,
  scrollPaddingRight: null,
  scrollPaddingTop: null,
  scrollSnapAlign: null,
  scrollSnapStop: null,
  scrollSnapType: null,
  scrollbarColor: null,
  scrollbarGutter: null,
  scrollbarWidth: null,
  shapeImageThreshold: null,
  shapeMargin: null,
  shapeOutside: null,
  shapeRendering: null,
  stopColor: null,
  stopOpacity: null,
  stroke: null,
  strokeDasharray: null,
  strokeDashoffset: null,
  strokeLinecap: null,
  strokeLinejoin: null,
  strokeMiterlimit: null,
  strokeOpacity: null,
  strokeWidth: null,
  tableLayout: null,
  tabSize: null,
  textAlign: null,
  textAlignLast: null,
  textAnchor: null,
  textCombineUpright: null,
  textDecorationColor: null,
  textDecorationLine: null,
  textDecorationSkip: null,
  textDecorationSkipInk: null,
  textDecorationStyle: null,
  textDecorationThickness: null,
  textEmphasisColor: null,
  textEmphasisPosition: null,
  textEmphasisStyle: null,
  textIndent: null,
  textJustify: null,
  textOrientation: null,
  textOverflow: null,
  textRendering: null,
  textShadow: null,
  textSizeAdjust: null,
  textTransform: null,
  textUnderlineOffset: null,
  textUnderlinePosition: null,
  textWrapMode: null,
  textWrapStyle: null,
  top: null,
  touchAction: null,
  transform: null,
  transformBox: null,
  transformOrigin: null,
  transformStyle: null,
  transitionDelay: null,
  transitionDuration: null,
  transitionProperty: null,
  transitionTimingFunction: null,
  translate: null,
  unicodeBidi: null,
  userSelect: null,
  vectorEffect: null,
  verticalAlign: null,
  visibility: null,
  whiteSpace: null,
  widows: null,
  width: null,
  willChange: null,
  wordBreak: null,
  wordSpacing: null,
  writingMode: null,
  x: null,
  y: null,
  zIndex: null,
};

export const knownProperties = new Set(Object.keys(style));
